import React, { useState, useEffect, useRef } from 'react';
import { API_URL, fmtFecha } from '../constants';
import { fileToBase64 } from './shared';

// ════════════════════════════════════════════════════════════════════════════
// EVALUACIONES DE DESEMPEÑO — bandeja de seguimiento RH (solo lectura)
//
// Consume exclusivamente el backend de SIG-RH (POST ?path=api/evaluaciones/listar,
// con el idToken de Google en el body — nunca en query string). SIG-RH es quien
// verifica identidad + whitelist de RH y hace la llamada server-to-server hacia
// el Apps Script externo de Evaluaciones con el secreto de integración; este
// componente nunca conoce ni SIGRH_INTEGRATION_TOKEN ni llama directo a
// Evaluaciones.
//
// Solo lectura por ahora: el backend todavía no tiene una acción de escritura
// para el seguimiento RH (estado/decisión/observaciones/salario), así que esta
// vista muestra ese seguimiento tal como está registrado, sin controles de
// edición — eso queda para el bloque en que se construya ese endpoint.
// ════════════════════════════════════════════════════════════════════════════

const SOLICITUD_LABELS = {
  cambio_departamento: 'Cambio de Departamento',
  cambio_area: 'Cambio de Área',
  continuidad: 'Continuidad',
  cambio_salario: 'Cambio de Salario',
  desarrollo: 'Desarrollo / Entrenamiento',
  rescision: 'Rescisión de contrato',
};

const RESOLUCION_BADGE = {
  PENDIENTE: 'badge-warning',
  APROBADO: 'badge-success',
  NO_APROBADO: 'badge-danger',
  NO_APLICA: null,
};

function badgeEstado(estatus) {
  const e = (estatus || '').toLowerCase();
  if (e === 'finalizada' || e === 'aprobada') return 'badge-success';
  if (e === 'rechazada') return 'badge-danger';
  return 'badge-warning'; // Pendiente, En revisión, Pendiente 1:1 / Firma, o cualquier valor futuro
}

function fmtSalario(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });
}

function solicitudesActivas(ev) {
  const s = ev.solicitudes || {};
  return Object.keys(SOLICITUD_LABELS).filter(k => s[k]);
}

const RESOLUCIONES_SELECCIONABLES = ['PENDIENTE', 'APROBADO', 'NO_APROBADO'];

// ── Lógica pura de Seguimiento RH — exportada para pruebas (ver
// Evaluaciones.test.js, mismo patrón que ImportacionIntellisis.jsx: sin
// @testing-library/react, funciones puras probadas directamente con Jest). ──

// Determina si una fecha "YYYY-MM-DD" cae en jueves sin depender de la zona
// horaria del navegador (misma lógica que el backend, ver esJuevesCalendario_
// en el Apps Script de Evaluaciones) — el backend es la autoridad final; esto
// es solo para dar retroalimentación inmediata en la UI.
export function esJuevesISO(fechaStr) {
  if (typeof fechaStr !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(fechaStr)) return false;
  const [anio, mes, dia] = fechaStr.split('-').map(Number);
  const fechaUTC = new Date(Date.UTC(anio, mes - 1, dia));
  if (fechaUTC.getUTCFullYear() !== anio || fechaUTC.getUTCMonth() !== mes - 1 || fechaUTC.getUTCDate() !== dia) return false;
  return fechaUTC.getUTCDay() === 4;
}

export function fmtSalarioInput(v) {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  return Number.isNaN(n) ? '' : n.toFixed(2);
}

// ((nuevo - actual) / actual) * 100, formateado a 2 decimales. null si no hay
// datos suficientes para calcularlo (nunca se persiste, solo se muestra).
export function calcularPorcentajeAumento(salarioActualNum, salarioNuevoNum) {
  if (Number.isNaN(salarioActualNum) || salarioActualNum <= 0 || Number.isNaN(salarioNuevoNum)) return null;
  return (((salarioNuevoNum - salarioActualNum) / salarioActualNum) * 100).toFixed(2);
}

// Único punto de verdad de qué falta para poder aprobar un cambio de
// salario — usado tanto para deshabilitar "Guardar seguimiento" como para
// decidir qué mensaje mostrar. El backend vuelve a validar todo esto de
// forma independiente (es la autoridad final); esto es solo para no dejar
// que RH intente guardar un estado que el servidor rechazará de todos modos.
export function faltanDatosAumento({ cambioSalarioAprobado, salarioConfirmado, salarioActualNum, salarioNuevoNum, fechaEfectiva }) {
  if (!cambioSalarioAprobado) return false;
  if (!salarioConfirmado) return true;
  if (Number.isNaN(salarioActualNum) || salarioActualNum <= 0) return true;
  if (Number.isNaN(salarioNuevoNum) || salarioNuevoNum <= 0) return true;
  if (!fechaEfectiva || !esJuevesISO(fechaEfectiva)) return true;
  return false;
}

// Compuerta de doble clic: además del `disabled` visual del botón (que
// depende de un re-render de React, con una ventana breve antes de
// reflejarse), esta función es la que realmente decide si se permite
// intentar un guardado — se prueba directamente para confirmar que un
// segundo intento mientras `guardando === true` nunca procede.
export function puedeIntentarGuardar(guardando, puedeGuardar) {
  return !guardando && puedeGuardar;
}

// Arma el body exacto que se envía al backend — solo incluye los campos de
// salario cuando el estado final es APROBADO, nunca "de más".
export function construirPayloadSeguimiento({ idEvaluacion, resolucion, observaciones, cambioSalarioAprobado, salarioActualNum, salarioNuevoNum, fechaEfectiva }) {
  const payload = {
    id_evaluacion: idEvaluacion,
    resolucion_rh: resolucion,
    observaciones_rh: observaciones
  };
  if (cambioSalarioAprobado) {
    payload.salario_anterior_confirmado = salarioActualNum;
    payload.salario_nuevo = salarioNuevoNum;
    payload.fecha_efectiva = fechaEfectiva;
  }
  return payload;
}

async function solicitarSalarioSugerido(idToken, idEmpleado) {
  const res = await fetch(API_URL + '?path=api/evaluaciones/salario-sugerido', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, id_empleado: idEmpleado })
  });
  return res.json();
}

// Orquestación pura del guardado: recibe fetchImpl inyectado (igual que
// ejecutarApply en ImportacionIntellisis.jsx) para poder probarse con Jest
// sin red real. NUNCA lanza — ante cualquier fallo (red, JSON inválido)
// devuelve {success:false,...} en vez de dejar que el error se propague y
// potencialmente pierda lo que el usuario capturó en el formulario.
export async function ejecutarGuardarSeguimiento(idToken, payload, fetchImpl) {
  try {
    const res = await fetchImpl(API_URL + '?path=api/evaluaciones/actualizar-seguimiento', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken, ...payload })
    });
    return await res.json();
  } catch {
    return { success: false, error: 'No se pudo conectar con el servidor.' };
  }
}

// ── Lógica pura de Carta de Aumento Salarial — exportada para pruebas ──────

// Único punto de verdad de si la sección debe mostrarse -- exclusivamente a
// partir del estado YA CONFIRMADO por servidor (ev.resolucion_rh), nunca de
// cambios locales todavía no guardados en el formulario de Seguimiento RH.
// El backend vuelve a validar esto de forma independiente (es la autoridad
// final); esto solo evita ofrecer el botón cuando el servidor de todos
// modos lo rechazaría.
export function puedeGenerarCarta(ev) {
  return !!(ev && ev.resolucion_rh && ev.resolucion_rh.cambio_salario === 'APROBADO');
}

// Único punto de verdad del estado de la carta -- el backend distingue 3
// estados (ver generarCartaAumento_ del Apps Script externo) y el frontend
// debe reflejarlos exactamente igual, nunca colapsarlos con una expresión
// como "documento_url || pdf_url" (eso trataría un estado PARCIAL como si
// fuera COMPLETA). Funciona tanto con el `ev` recién cargado de
// listarEvaluacionesSIGRH como con el `ev` actualizado tras una generación
// -- ambos exponen los mismos 2 campos de trazabilidad.
export function obtenerEstadoCarta(ev) {
  const tieneDoc = !!(ev && ev.documento_aumento_url);
  const tienePdf = !!(ev && ev.pdf_aumento_url);
  if (tieneDoc && tienePdf) return 'COMPLETA';
  if (tieneDoc || tienePdf) return 'PARCIAL';
  return 'NINGUNA';
}

export function tieneCartaCompleta(ev) {
  return obtenerEstadoCarta(ev) === 'COMPLETA';
}

export function tieneCartaParcial(ev) {
  return obtenerEstadoCarta(ev) === 'PARCIAL';
}

// Mismo criterio que el backend -- no deja avanzar con datos mensuales
// obviamente incompletos cuando la variante mensual está activa. Si la
// variante es DIARIO, nunca "falta" nada aquí (los campos mensuales ni
// siquiera se muestran).
export function faltanSalariosMensuales(incluirMensual, salarioMensualActualNum, salarioMensualNuevoNum) {
  if (!incluirMensual) return false;
  if (Number.isNaN(salarioMensualActualNum) || salarioMensualActualNum <= 0) return true;
  if (Number.isNaN(salarioMensualNuevoNum) || salarioMensualNuevoNum <= 0) return true;
  return false;
}

// Arma el body exacto -- variante DIARIO nunca incluye salarios mensuales,
// DIARIO_MENSUAL siempre incluye ambos. Nunca incluye datos de negocio
// persistidos (nombre, salarios diarios, evaluador, RH original...) -- el
// backend los localiza por su cuenta a partir de id_evaluacion.
export function construirPayloadCartaAumento({ idEvaluacion, incluirMensual, salarioMensualActualNum, salarioMensualNuevoNum }) {
  const payload = { id_evaluacion: idEvaluacion, variante: incluirMensual ? 'DIARIO_MENSUAL' : 'DIARIO' };
  if (incluirMensual) {
    payload.salario_mensual_actual = salarioMensualActualNum;
    payload.salario_mensual_nuevo = salarioMensualNuevoNum;
  }
  return payload;
}

// Orquestación pura, fetchImpl inyectado -- mismo patrón que
// ejecutarGuardarSeguimiento. NUNCA lanza.
export async function ejecutarGenerarCartaAumento(idToken, payload, fetchImpl) {
  try {
    const res = await fetchImpl(API_URL + '?path=api/evaluaciones/generar-carta-aumento', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken, ...payload })
    });
    return await res.json();
  } catch {
    return { success: false, error: 'No se pudo conectar con el servidor.' };
  }
}

// ── Lógica pura de Corrección de RH de Evaluación — exportada para pruebas ──

// Único punto de verdad de si la evaluación ya tiene un RH efectivo
// (corregido o resuelto del original) -- el backend ya calcula esta
// prioridad (corregido > original > "") y la expone en ev.rh_efectivo; el
// frontend NUNCA reimplementa esa lógica, solo lee el resultado.
export function tieneRhEfectivo(ev) {
  return !!(ev && ev.rh_efectivo);
}

// Arma el body exacto de la corrección -- nunca incluye datos de negocio
// persistidos aparte de la selección; usuario_correccion NUNCA sale de
// aquí (el backend lo deriva del idToken verificado).
export function construirPayloadCorregirRh({ idEvaluacion, rhSeleccionado }) {
  return { id_evaluacion: idEvaluacion, rh_seleccionado: rhSeleccionado };
}

// Orquestación pura, fetchImpl inyectado -- mismo patrón que
// ejecutarGenerarCartaAumento. NUNCA lanza.
export async function ejecutarCorregirRhEvaluacion(idToken, payload, fetchImpl) {
  try {
    const res = await fetchImpl(API_URL + '?path=api/evaluaciones/corregir-rh-evaluacion', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken, ...payload })
    });
    return await res.json();
  } catch {
    return { success: false, error: 'No se pudo conectar con el servidor.' };
  }
}

// ── Lógica pura de Cierre de Evaluación (Bloque B) — exportada para pruebas ──
//
// Alcance de esta fase: visualizar estado de cierre, adjuntar evidencia
// firmada, visualizar evidencia existente y finalizar. NO incluye
// reemplazo de evidencia, reapertura, eliminación, drag-and-drop, múltiples
// archivos, HEIC ni DOCX -- eso queda fuera de este bloque.

const MIME_EVIDENCIA_PERMITIDOS = { 'application/pdf': true, 'image/jpeg': true, 'image/png': true };
const TAMANO_MAXIMO_EVIDENCIA_BYTES = 5 * 1024 * 1024;
// Único mensaje que ejecutarSubirEvidencia/ejecutarFinalizarEvaluacion
// devuelven ante un fallo de transporte (fetch rechazado, JSON no
// interpretable) -- ver esRespuestaAmbigua más abajo. Es el mismo texto
// genérico que ya usan ejecutarGuardarSeguimiento/ejecutarGenerarCartaAumento/
// ejecutarCorregirRhEvaluacion arriba en este archivo.
const MENSAJE_TRANSPORTE_AMBIGUO = 'No se pudo conectar con el servidor.';

// Estado de la evidencia firmada -- equivalente conceptual exacto de
// obtenerEstadoEvidenciaFirma_ del Apps Script externo: mismos 5 campos,
// mismo criterio (0 presentes -> NINGUNA, 5 presentes -> COMPLETA,
// cualquier otra combinación -> PARCIAL). El backend es la autoridad; esto
// es solo para reflejar el mismo estado en la UI sin reimplementar la
// decisión de negocio de forma distinta.
export function obtenerEstadoEvidencia(ev) {
  if (!ev) return 'NINGUNA';
  const campos = [
    ev.evidencia_firma_url,
    ev.nombre_archivo_evidencia,
    ev.id_archivo_evidencia,
    ev.fecha_subida_evidencia,
    ev.usuario_subida_evidencia
  ];
  const presentes = campos.filter(Boolean).length;
  if (presentes === 0) return 'NINGUNA';
  if (presentes === 5) return 'COMPLETA';
  return 'PARCIAL';
}

// Validación local de MIME + tamaño -- espejo exacto de
// validarArchivoEvidencia_ del Apps Script externo (mismos MIME
// permitidos, mismo límite de 5MB). Esto es UX solamente: el backend
// vuelve a validar todo de forma independiente y sigue siendo la
// autoridad final -- esta función nunca decide si algo se persiste.
export function validarArchivoEvidencia(mime, tamanoBytes) {
  if (!mime || !MIME_EVIDENCIA_PERMITIDOS[mime]) {
    return { ok: false, error: 'Tipo de archivo no permitido. Usa PDF, JPG o PNG.' };
  }
  if (!tamanoBytes || tamanoBytes === 0) {
    return { ok: false, error: 'El archivo está vacío.' };
  }
  if (tamanoBytes > TAMANO_MAXIMO_EVIDENCIA_BYTES) {
    return { ok: false, error: 'El archivo supera el tamaño máximo permitido (5 MB).' };
  }
  return { ok: true };
}

// fileToBase64 (./shared, compartido con Capacitaciones.jsx/Empleados.jsx
// -- NUNCA se modifica aquí) devuelve el Data URL COMPLETO de
// FileReader.readAsDataURL ("data:<mime>;base64,<datos>"), nunca base64
// puro. Utilities.base64Decode() del backend (subirEvidencia_ del Apps
// Script externo) espera base64 puro -- el mismo prefijo se descarta ya
// en Capacitaciones.jsx/Empleados.jsx con `.split(',')[1]`; aquí se
// extrae como función pura propia de este flujo para poder probarlo
// explícitamente sin duplicar la lógica de fileToBase64 en sí.
export function extraerBase64Puro(dataUrl) {
  const str = String(dataUrl || '');
  const idx = str.indexOf(',');
  return idx === -1 ? str : str.slice(idx + 1);
}

// Arma el body exacto de subir-evidencia -- nunca incluye
// usuario_subida_evidencia (SIG-RH lo deriva del idToken verificado,
// nunca de lo que React mande) ni el token de integración (React no lo
// conoce).
export function construirPayloadSubirEvidencia({ idEvaluacion, archivoBase64, archivoMime, archivoNombreOriginal }) {
  return {
    id_evaluacion: idEvaluacion,
    archivo_base64: archivoBase64,
    archivo_mime: archivoMime,
    archivo_nombre_original: archivoNombreOriginal || ''
  };
}

// Orquestación pura de la llamada de red, fetchImpl inyectado -- mismo
// patrón que ejecutarGuardarSeguimiento/ejecutarGenerarCartaAumento.
// NUNCA lanza: ante fetch rechazado o JSON no interpretable devuelve un
// objeto marcado con _fallaTransporte:true (ver esRespuestaAmbigua -- la
// clasificación es ESTRUCTURAL, nunca por el texto de `.error`).
export async function ejecutarSubirEvidencia(idToken, payload, fetchImpl) {
  try {
    const res = await fetchImpl(API_URL + '?path=api/evaluaciones/subir-evidencia', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken, ...payload })
    });
    return await res.json();
  } catch {
    return { success: false, error: MENSAJE_TRANSPORTE_AMBIGUO, _fallaTransporte: true };
  }
}

// Arma el body exacto de finalizar -- nunca incluye
// usuario_cierre_evaluacion (SIG-RH lo deriva del idToken).
export function construirPayloadFinalizarEvaluacion({ idEvaluacion }) {
  return { id_evaluacion: idEvaluacion };
}

// Orquestación pura, mismo patrón que ejecutarSubirEvidencia.
export async function ejecutarFinalizarEvaluacion(idToken, payload, fetchImpl) {
  try {
    const res = await fetchImpl(API_URL + '?path=api/evaluaciones/finalizar', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken, ...payload })
    });
    return await res.json();
  } catch {
    return { success: false, error: MENSAJE_TRANSPORTE_AMBIGUO, _fallaTransporte: true };
  }
}

// Refresco independiente del listado completo -- usado por el flujo de
// recuperación ante respuesta ambigua (secciones 9/10/15/16 del plan de
// Fase 4), nunca se asume el estado local tras un upload/finalizar. Mismo
// endpoint que usa el `cargar()` inicial del componente, expuesto aquí
// como función pura con fetchImpl inyectado para poder probarse y para
// poder reutilizarse desde la recuperación sin depender del ciclo de vida
// del componente principal.
export async function consultarEvaluaciones(idToken, fetchImpl) {
  try {
    const res = await fetchImpl(API_URL + '?path=api/evaluaciones/listar', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken })
    });
    const data = await res.json();
    if (data.success) {
      return { ok: true, evaluaciones: data.evaluaciones || [], catalogoRh: data.catalogo_rh || [] };
    }
    return { ok: false, error: data.error || 'No fue posible consultar Evaluaciones' };
  } catch {
    return { ok: false, error: MENSAJE_TRANSPORTE_AMBIGUO };
  }
}

// Localiza EXACTAMENTE una evaluación por id_evaluacion en una lista recién
// refrescada del servidor. Sabemos que existen IDs históricos duplicados
// (EVA-0003/EVA-0004) -- esta función NUNCA toma "la primera coincidencia":
// si hay 0 o 2+ coincidencias, la evaluación no puede identificarse de
// forma inequívoca y se devuelve null. El llamador debe tratar null como
// "no se pudo confirmar", nunca asumir cuál de los duplicados es el
// correcto. En la práctica, el backend ya rechaza subirEvidencia_/
// cerrarEvaluacion_ para IDs duplicados (resolverFilaEvaluacionUnica_), así
// que un success:true en la respuesta directa implica unicidad -- pero
// esta función igual revalida contra la lista fresca, sin confiar en esa
// suposición.
export function localizarEvaluacionUnica(evaluaciones, idEvaluacion) {
  const coincidencias = (evaluaciones || []).filter(e => e && e.id_evaluacion === idEvaluacion);
  return coincidencias.length === 1 ? coincidencias[0] : null;
}

// Interpreta el estado de evidencia de UNA evaluación ya localizada de
// forma inequívoca en la lista fresca -- usado tanto tras una subida
// exitosa como tras una respuesta ambigua (sección 9/10).
export function interpretarEvidenciaTrasRefresco(evaluaciones, idEvaluacion) {
  const ev = localizarEvaluacionUnica(evaluaciones, idEvaluacion);
  if (!ev) return { resultado: 'NO_IDENTIFICABLE', evaluacion: null };
  const estado = obtenerEstadoEvidencia(ev);
  if (estado === 'COMPLETA') return { resultado: 'EXITO_RECUPERADO', evaluacion: ev };
  if (estado === 'NINGUNA') return { resultado: 'NO_CONFIRMADO', evaluacion: ev };
  return { resultado: 'PARCIAL', evaluacion: ev };
}

// Mismo criterio que interpretarEvidenciaTrasRefresco pero para el cierre
// (sección 15/16): éxito recuperado solo si ESTATUS === "Finalizada" en la
// lista fresca.
export function interpretarCierreTrasRefresco(evaluaciones, idEvaluacion) {
  const ev = localizarEvaluacionUnica(evaluaciones, idEvaluacion);
  if (!ev) return { resultado: 'NO_IDENTIFICABLE', evaluacion: null };
  if (ev.estatus === 'Finalizada') return { resultado: 'EXITO_RECUPERADO', evaluacion: ev };
  return { resultado: 'NO_CONFIRMADO', evaluacion: ev };
}

// Distingue una respuesta AMBIGUA (fetch rechazado, JSON no interpretable,
// o cualquier otra forma que no permita saber si el servidor completó la
// operación) de un rechazo de negocio REAL y explícito (success:false ya
// parseado desde una respuesta HTTP real). Esta distinción es la sección
// 10 del plan de Fase 4, motivada por un falso negativo histórico ya
// ocurrido en Carta de Aumento: el backend completó la operación pero el
// navegador mostró error de conexión.
//
// La clasificación es ESTRUCTURAL, nunca por el TEXTO de `.error`:
// - `_fallaTransporte` (puesto únicamente por el catch de
//   ejecutarSubirEvidencia/ejecutarFinalizarEvaluacion cuando fetch
//   rechaza o el cuerpo no se pudo parsear) -> siempre ambigua.
// - Cualquier objeto con `success` como boolean real es una respuesta
//   CONOCIDA del servidor -- éxito o rechazo -- sin importar qué diga
//   `.error` (incluso si por coincidencia el backend usara literalmente
//   el mismo texto que MENSAJE_TRANSPORTE_AMBIGUO, seguiría sin ser
//   ambigua: sí hubo una respuesta HTTP real y parseable).
// - Cualquier otra forma (sin `success` boolean, JSON parseado pero
//   irreconocible) -> ambigua, porque no permite determinar
//   success true/false.
export function esRespuestaAmbigua(resultado) {
  if (!resultado || typeof resultado !== 'object') return true;
  if (resultado._fallaTransporte) return true;
  return typeof resultado.success !== 'boolean';
}

// Orquesta el flujo completo de "adjuntar evidencia": sube el archivo y,
// tanto si la respuesta es ambigua como si es un success:true directo,
// SIEMPRE refresca desde servidor y solo confirma éxito si la evidencia
// queda COMPLETA ahí (sección 9: "no asumir únicamente el estado local").
// Un rechazo de negocio real (no ambiguo) se devuelve tal cual, sin
// refrescar ni reintentar. onFase (opcional) permite que el componente
// refleje la transición "subiendo" -> "verificando" en la UI sin que la
// lógica de decisión deba vivir ahí. fetchImpl inyectado para pruebas.
export async function ejecutarFlujoSubirEvidencia(idToken, payload, idEvaluacion, fetchImpl, onFase) {
  const resultado = await ejecutarSubirEvidencia(idToken, payload, fetchImpl);
  if (!esRespuestaAmbigua(resultado) && resultado.success !== true) {
    return { resultado: 'RECHAZADO', error: resultado.error || 'No fue posible subir la evidencia.' };
  }
  if (typeof onFase === 'function') onFase('verificando');
  const refrescar = await consultarEvaluaciones(idToken, fetchImpl);
  if (!refrescar.ok) {
    return { resultado: 'SIN_CONFIRMAR', error: 'No fue posible confirmar si la evidencia se adjuntó. Actualiza la información antes de volver a intentarlo.' };
  }
  const interp = interpretarEvidenciaTrasRefresco(refrescar.evaluaciones, idEvaluacion);
  if (interp.resultado === 'EXITO_RECUPERADO') return { resultado: 'EXITO', evaluacion: interp.evaluacion };
  if (interp.resultado === 'NO_CONFIRMADO') return { resultado: 'NO_CONFIRMADO', error: 'No se pudo confirmar la carga de la evidencia. Puedes intentarlo nuevamente.' };
  // Se incluye la evaluación fresca (PARCIAL) también en INTEGRIDAD -- el
  // llamador debe reflejar este estado en el `ev` del componente padre
  // (vía onActualizado) para que el bloqueo persista en el siguiente
  // render, no solo mientras dura este mensaje de error local.
  if (interp.resultado === 'PARCIAL') return { resultado: 'INTEGRIDAD', error: 'La evidencia quedó en un estado inconsistente. Contacta a TI antes de continuar.', evaluacion: interp.evaluacion };
  return { resultado: 'SIN_CONFIRMAR', error: 'No fue posible confirmar el estado de la evidencia de forma inequívoca. Actualiza antes de continuar.' };
}

// Mismo patrón que ejecutarFlujoSubirEvidencia, para el cierre de la
// evaluación (secciones 15/16).
export async function ejecutarFlujoFinalizarEvaluacion(idToken, idEvaluacion, fetchImpl, onFase) {
  const resultado = await ejecutarFinalizarEvaluacion(idToken, construirPayloadFinalizarEvaluacion({ idEvaluacion }), fetchImpl);
  if (!esRespuestaAmbigua(resultado) && resultado.success !== true) {
    return { resultado: 'RECHAZADO', error: resultado.error || 'No fue posible cerrar la evaluación.' };
  }
  if (typeof onFase === 'function') onFase('verificando');
  const refrescar = await consultarEvaluaciones(idToken, fetchImpl);
  if (!refrescar.ok) {
    return { resultado: 'SIN_CONFIRMAR', error: 'No fue posible confirmar si la evaluación se finalizó. Actualiza la información antes de volver a intentarlo.' };
  }
  const interp = interpretarCierreTrasRefresco(refrescar.evaluaciones, idEvaluacion);
  if (interp.resultado === 'EXITO_RECUPERADO') return { resultado: 'EXITO', evaluacion: interp.evaluacion };
  if (interp.resultado === 'NO_CONFIRMADO') return { resultado: 'NO_CONFIRMADO', error: 'No se confirmó el cierre de la evaluación. Puedes intentarlo nuevamente.' };
  return { resultado: 'SIN_CONFIRMAR', error: 'No fue posible confirmar el estado de la evaluación de forma inequívoca. Actualiza antes de continuar.' };
}

// ════════════════════════════════════════════════════════════════════════════
// CARTA DE AUMENTO FIRMADA (Bloque B.1) -- SEGUNDA evidencia, conceptualmente
// distinta de (A) la evaluación firmada (BN:BR, obtenerEstadoEvidencia) y de
// (B) la carta de aumento GENERADA (BF/BG, obtenerEstadoCarta). Aplica solo
// cuando el estado confirmado por servidor es
// resolucion_rh.cambio_salario === "APROBADO". El backend externo V18 es la
// autoridad final de todas las reglas; esto solo refleja su semántica en la
// UI y evita ofrecer acciones que el servidor rechazaría de todos modos.
// ════════════════════════════════════════════════════════════════════════════

// Única fuente de verdad de si aplica la Carta firmada -- exclusivamente
// resolucion_rh.cambio_salario (nunca cambio_salario_control, nunca el
// salario nuevo, nunca la existencia de BF/BG).
export function requiereCartaAumentoFirmada(ev) {
  return !!(ev && ev.resolucion_rh && ev.resolucion_rh.cambio_salario === 'APROBADO');
}

// Equivalente exacto de obtenerEstadoCartaFirmada_ del Apps Script externo
// V18 (BU:BY): 0/5 -> NINGUNA, 5/5 -> COMPLETA, cualquier otra -> PARCIAL.
export function obtenerEstadoCartaFirmada(ev) {
  if (!ev) return 'NINGUNA';
  const campos = [
    ev.evidencia_carta_firmada_url,
    ev.nombre_archivo_carta_firmada,
    ev.id_archivo_carta_firmada,
    ev.fecha_subida_carta_firmada,
    ev.usuario_subida_carta_firmada
  ];
  const presentes = campos.filter(Boolean).length;
  if (presentes === 0) return 'NINGUNA';
  if (presentes === 5) return 'COMPLETA';
  return 'PARCIAL';
}

// Requisitos para OFRECER "Finalizar evaluación" (no considera el ESTATUS:
// eso lo decide derivarVistaCierre). Toda evaluación exige la evaluación
// firmada COMPLETA; si además hay aumento APROBADO exige Carta generada
// (BF+BG) COMPLETA y Carta firmada COMPLETA. Cualquier PARCIAL bloquea. El
// backend vuelve a validar todo (cerrarEvaluacion_) y sigue siendo autoridad.
export function puedeFinalizarEvaluacion(ev) {
  if (obtenerEstadoEvidencia(ev) !== 'COMPLETA') return false;
  if (!requiereCartaAumentoFirmada(ev)) return true;
  return obtenerEstadoCarta(ev) === 'COMPLETA' && obtenerEstadoCartaFirmada(ev) === 'COMPLETA';
}

// Modelo de vista PURO de la sección "Cierre de evaluación": el componente
// solo renderiza lo que esto decide, de modo que cada rama es probable con
// Jest sin @testing-library/react.
export function derivarVistaCierre(ev) {
  const estatus = ev && ev.estatus;
  const esPendienteFirma = estatus === 'Pendiente 1:1 / Firma';
  const esFinalizada = estatus === 'Finalizada';
  const estadoEvidencia = obtenerEstadoEvidencia(ev);
  const requiereCarta = requiereCartaAumentoFirmada(ev);
  const estadoCartaGenerada = obtenerEstadoCarta(ev);
  const estadoCartaFirmada = obtenerEstadoCartaFirmada(ev);
  const base = { estadoEvidencia, requiereCarta, estadoCartaGenerada, estadoCartaFirmada };

  if (!esPendienteFirma && !esFinalizada) return { ...base, modo: 'OCULTO' };
  // Finalizada histórica (sin evidencia): nunca error, nunca se exige nada
  // retroactivamente (ni evaluación firmada, ni Carta generada, ni firmada).
  if (esFinalizada && estadoEvidencia === 'NINGUNA') return { ...base, modo: 'OCULTO' };
  // Finalizada + evidencia PARCIAL es una anomalía real. En Pendiente 1:1 /
  // Firma el PARCIAL de una evidencia NO bloquea la otra (independientes).
  if (esFinalizada && estadoEvidencia === 'PARCIAL') return { ...base, modo: 'INTEGRIDAD_EVIDENCIA' };

  if (esFinalizada) {
    return {
      ...base,
      modo: 'FINALIZADA',
      mostrarCartaFirmada: requiereCarta && estadoCartaFirmada === 'COMPLETA',
      integridadCartaFirmada: requiereCarta && estadoCartaFirmada === 'PARCIAL'
    };
  }

  return {
    ...base,
    modo: 'PENDIENTE_FIRMA',
    puedeSubirEvidencia: estadoEvidencia === 'NINGUNA',
    bloqueoIntegridadEvidencia: estadoEvidencia === 'PARCIAL',
    // Las dos evidencias son independientes: ninguna depende de la otra, de
    // BF/BG ni de la Carta generada. Solo estatus + APROBADO + NINGUNA.
    puedeSubirCartaFirmada: requiereCarta && estadoCartaFirmada === 'NINGUNA',
    bloqueoIntegridadCartaFirmada: requiereCarta && estadoCartaFirmada === 'PARCIAL',
    advertenciaCartaGeneradaParcial: requiereCarta && estadoCartaGenerada === 'PARCIAL',
    puedeFinalizar: puedeFinalizarEvaluacion(ev)
  };
}

// Body exacto de subir-carta-firmada -- nunca incluye identidad RH
// (usuario_*, email) ni el token de integración: SIG-RH V89 deriva el nombre
// del idToken verificado.
export function construirPayloadSubirCartaFirmada({ idEvaluacion, archivoBase64, archivoMime, archivoNombreOriginal }) {
  return {
    id_evaluacion: idEvaluacion,
    archivo_base64: archivoBase64,
    archivo_mime: archivoMime,
    archivo_nombre_original: archivoNombreOriginal || ''
  };
}

// Mismo patrón que ejecutarSubirEvidencia (nunca lanza; fallo de transporte
// marcado estructuralmente con _fallaTransporte).
export async function ejecutarSubirCartaFirmada(idToken, payload, fetchImpl) {
  try {
    const res = await fetchImpl(API_URL + '?path=api/evaluaciones/subir-carta-firmada', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken, ...payload })
    });
    return await res.json();
  } catch {
    return { success: false, error: MENSAJE_TRANSPORTE_AMBIGUO, _fallaTransporte: true };
  }
}

// Interpreta el estado de la Carta firmada en la lista fresca (tras éxito
// directo o respuesta ambigua).
export function interpretarCartaFirmadaTrasRefresco(evaluaciones, idEvaluacion) {
  const ev = localizarEvaluacionUnica(evaluaciones, idEvaluacion);
  if (!ev) return { resultado: 'NO_IDENTIFICABLE', evaluacion: null };
  const estado = obtenerEstadoCartaFirmada(ev);
  if (estado === 'COMPLETA') return { resultado: 'EXITO_RECUPERADO', evaluacion: ev };
  if (estado === 'NINGUNA') return { resultado: 'NO_CONFIRMADO', evaluacion: ev };
  return { resultado: 'PARCIAL', evaluacion: ev };
}

// Misma filosofía que ejecutarFlujoSubirEvidencia: success:false conocido =
// rechazo sin refrescar; éxito directo o ambigüedad = refrescar y confirmar
// solo si la Carta firmada queda COMPLETA. Nunca auto-reintenta.
export async function ejecutarFlujoSubirCartaFirmada(idToken, payload, idEvaluacion, fetchImpl, onFase) {
  const resultado = await ejecutarSubirCartaFirmada(idToken, payload, fetchImpl);
  if (!esRespuestaAmbigua(resultado) && resultado.success !== true) {
    return { resultado: 'RECHAZADO', error: resultado.error || 'No fue posible subir la carta de aumento firmada.' };
  }
  if (typeof onFase === 'function') onFase('verificando');
  const refrescar = await consultarEvaluaciones(idToken, fetchImpl);
  if (!refrescar.ok) {
    return { resultado: 'SIN_CONFIRMAR', error: 'No fue posible confirmar si la carta firmada se adjuntó. Actualiza la información antes de volver a intentarlo.' };
  }
  const interp = interpretarCartaFirmadaTrasRefresco(refrescar.evaluaciones, idEvaluacion);
  if (interp.resultado === 'EXITO_RECUPERADO') return { resultado: 'EXITO', evaluacion: interp.evaluacion };
  if (interp.resultado === 'NO_CONFIRMADO') return { resultado: 'NO_CONFIRMADO', error: 'No se pudo confirmar la carga de la carta firmada. Puedes intentarlo nuevamente.' };
  if (interp.resultado === 'PARCIAL') return { resultado: 'INTEGRIDAD', error: 'La carta de aumento firmada quedó en un estado inconsistente. Contacta a TI antes de continuar.', evaluacion: interp.evaluacion };
  return { resultado: 'SIN_CONFIRMAR', error: 'No fue posible confirmar el estado de la carta firmada de forma inequívoca. Actualiza antes de continuar.' };
}

// Filtro inicial de la sección Evaluaciones: "ACTIVAS" es un concepto de UI
// (no un estatus real -- nunca se escribe ni se compara contra el backend
// como tal). Agrupa únicamente para la vista inicial las evaluaciones que
// todavía requieren atención de RH. Pendiente y Pendiente 1:1 / Firma
// SIGUEN siendo dos estatus reales distintos, sin fusionar ni renombrar.
export const FILTRO_ACTIVAS = '__ACTIVAS__';
const ESTATUS_ACTIVOS = ['Pendiente', 'Pendiente 1:1 / Firma'];

export function esEstatusActivo(estatus) {
  return ESTATUS_ACTIVOS.includes(estatus);
}

export function Evaluaciones({ idToken }) {
  const [estadoCarga, setEstadoCarga] = useState('cargando'); // cargando | ok | no-autorizado | error
  const [mensajeError, setMensajeError] = useState('');
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [catalogoRh, setCatalogoRh] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  // Por defecto, "Activas" (Pendiente + Pendiente 1:1 / Firma) -- nunca
  // "Todos" como valor inicial silencioso: el <select> debe reflejar
  // fielmente lo que la tabla muestra (sección "Filtro inicial").
  const [filtroEstado, setFiltroEstado] = useState(FILTRO_ACTIVAS);
  const [filtroEvaluador, setFiltroEvaluador] = useState('');
  const [filtroPlanta, setFiltroPlanta] = useState('');
  const [detalle, setDetalle] = useState(null);

  const cargar = async () => {
    setEstadoCarga('cargando');
    setMensajeError('');
    try {
      const res = await fetch(API_URL + '?path=api/evaluaciones/listar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken })
      });
      const data = await res.json();
      if (data.success) {
        setEvaluaciones(data.evaluaciones || []);
        setCatalogoRh(data.catalogo_rh || []);
        setEstadoCarga('ok');
      } else if (data.error === 'No autorizado') {
        setEstadoCarga('no-autorizado');
      } else {
        setMensajeError(data.error || 'No fue posible consultar Evaluaciones');
        setEstadoCarga('error');
      }
    } catch {
      setMensajeError('No se pudo conectar con el servidor.');
      setEstadoCarga('error');
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleActualizado = (evaluacionActualizada) => {
    setEvaluaciones(prev => prev.map(e => e.id_evaluacion === evaluacionActualizada.id_evaluacion ? evaluacionActualizada : e));
    setDetalle(evaluacionActualizada);
  };

  // ── KPIs dinámicos por estado (no se asume un catálogo fijo) ──
  const conteoPorEstado = {};
  evaluaciones.forEach(ev => {
    const k = ev.estatus || 'Sin estado';
    conteoPorEstado[k] = (conteoPorEstado[k] || 0) + 1;
  });

  // ── Opciones de filtro derivadas de los datos cargados ──
  const opcionesEstado = Object.keys(conteoPorEstado).sort();
  const opcionesEvaluador = [...new Set(evaluaciones.map(e => e.nombre_evaluador).filter(Boolean))].sort();
  const opcionesPlanta = [...new Set(evaluaciones.map(e => e.planta).filter(Boolean))].sort();

  const filtradas = evaluaciones.filter(ev => {
    if (filtroEstado === FILTRO_ACTIVAS) {
      if (!esEstatusActivo(ev.estatus)) return false;
    } else if (filtroEstado && ev.estatus !== filtroEstado) {
      return false;
    }
    if (filtroEvaluador && ev.nombre_evaluador !== filtroEvaluador) return false;
    if (filtroPlanta && ev.planta !== filtroPlanta) return false;
    if (busqueda) {
      const q = busqueda.toLowerCase();
      const enNombre = (ev.nombre || '').toLowerCase().includes(q);
      const enId = String(ev.id_empleado || '').toLowerCase().includes(q);
      const enEval = String(ev.id_evaluacion || '').toLowerCase().includes(q);
      if (!enNombre && !enId && !enEval) return false;
    }
    return true;
  });

  const thStyle = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: '12px', color: '#4b5563', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '10px 12px', fontSize: '13px', color: '#1f2937', borderBottom: '1px solid #f3f4f6' };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#1d3d6b', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="ti ti-clipboard-check"></i> Evaluaciones de Desempeño
          </h1>
          <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '2px' }}>Seguimiento de RH sobre las evaluaciones recibidas del sistema externo</p>
        </div>
        <button className="btn-secondary" onClick={cargar} disabled={estadoCarga === 'cargando'} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <i className="ti ti-refresh"></i> {estadoCarga === 'cargando' ? 'Cargando...' : 'Recargar'}
        </button>
      </div>

      {estadoCarga === 'cargando' && (
        <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Cargando evaluaciones...</div>
      )}

      {estadoCarga === 'no-autorizado' && (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <i className="ti ti-lock" style={{ fontSize: '32px', color: '#9ca3af' }}></i>
          <div style={{ fontWeight: 700, color: '#1d3d6b', marginTop: '12px', fontSize: '15px' }}>No tienes acceso a este módulo</div>
          <div style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px' }}>
            Evaluaciones de Desempeño está limitado a Recursos Humanos. Si crees que deberías tener acceso, contacta al equipo de RH.
          </div>
        </div>
      )}

      {estadoCarga === 'error' && (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <i className="ti ti-alert-triangle" style={{ fontSize: '32px', color: '#f59e0b' }}></i>
          <div style={{ fontWeight: 700, color: '#1d3d6b', marginTop: '12px', fontSize: '15px' }}>No se pudo cargar Evaluaciones</div>
          <div style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px' }}>{mensajeError}</div>
        </div>
      )}

      {estadoCarga === 'ok' && (
        <>
          {/* KPIs dinámicos */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <div className="kpi" style={{ borderLeftColor: '#0086ab' }}>
              <div className="kpi-label"><i className="ti ti-list-check"></i> Total</div>
              <div className="kpi-val">{evaluaciones.length}</div>
            </div>
            {Object.keys(conteoPorEstado).sort().map(k => (
              <div key={k} className="kpi" style={{ borderLeftColor: badgeEstado(k) === 'badge-success' ? '#059669' : badgeEstado(k) === 'badge-danger' ? '#dc2626' : '#d97706' }}>
                <div className="kpi-label"><i className="ti ti-circle-dot"></i> {k}</div>
                <div className="kpi-val">{conteoPorEstado[k]}</div>
              </div>
            ))}
          </div>

          {/* Filtros */}
          <div className="card" style={{ marginBottom: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label>Buscar</label>
                <input type="text" placeholder="Empleado, # empleado o ID evaluación..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Estado</label>
                <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
                  <option value={FILTRO_ACTIVAS}>Activas (Pendiente + Pendiente 1:1 / Firma)</option>
                  <option value="">Todos</option>
                  {opcionesEstado.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Evaluador</label>
                <select value={filtroEvaluador} onChange={e => setFiltroEvaluador(e.target.value)}>
                  <option value="">Todos</option>
                  {opcionesEvaluador.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Planta</label>
                <select value={filtroPlanta} onChange={e => setFiltroPlanta(e.target.value)}>
                  <option value="">Todas</option>
                  {opcionesPlanta.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Tabla */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>ID</th>
                    <th style={thStyle}>Fecha</th>
                    <th style={thStyle}>Empleado</th>
                    <th style={thStyle}># Emp</th>
                    <th style={thStyle}>Puesto</th>
                    <th style={thStyle}>Planta</th>
                    <th style={thStyle}>Evaluador</th>
                    <th style={thStyle}>Calif.</th>
                    <th style={thStyle}>Nivel</th>
                    <th style={thStyle}>Solicitudes</th>
                    <th style={thStyle}>Estado</th>
                    <th style={thStyle}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtradas.length === 0 && (
                    <tr><td colSpan={12} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '30px' }}>Sin evaluaciones que coincidan con los filtros.</td></tr>
                  )}
                  {filtradas.map(ev => {
                    const sols = solicitudesActivas(ev);
                    return (
                      <tr key={ev.id_evaluacion} style={{ cursor: 'pointer' }} onClick={() => setDetalle(ev)}>
                        <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#6b7280' }}>{ev.id_evaluacion}</td>
                        <td style={tdStyle}>{fmtFecha(ev.fecha_evaluacion)}</td>
                        <td style={{ ...tdStyle, fontWeight: 500 }}>{ev.nombre}</td>
                        <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#6b7280' }}>#{ev.id_empleado}</td>
                        <td style={tdStyle}>{ev.puesto}</td>
                        <td style={tdStyle}>{ev.planta}</td>
                        <td style={tdStyle}>{ev.nombre_evaluador}</td>
                        <td style={{ ...tdStyle, fontWeight: 700, color: '#0086ab' }}>{ev.calificacion_final ?? '—'}</td>
                        <td style={tdStyle}>{ev.nivel}</td>
                        <td style={{ ...tdStyle, fontSize: '12px', color: '#6b7280' }}>
                          {sols.length ? sols.map(k => SOLICITUD_LABELS[k]).join(', ') : '—'}
                        </td>
                        <td style={tdStyle}><span className={`badge ${badgeEstado(ev.estatus)}`}>{ev.estatus || 'Sin estado'}</span></td>
                        <td style={tdStyle}><i className="ti ti-chevron-right" style={{ color: '#9ca3af' }}></i></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {detalle && (
        <ModalDetalleEvaluacion
          evaluacion={detalle}
          idToken={idToken}
          catalogoRh={catalogoRh}
          onActualizado={handleActualizado}
          onClose={() => setDetalle(null)}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MODAL DETALLE — expediente de seguimiento (Bloque B.1, Fase C.2)
//
// Reorganización SOLO de presentación: cabecera compacta, "Resumen de
// evaluación" y tres acordeones operativos independientes (Seguimiento RH,
// Carta de aumento salarial, Cierre de evaluación). Ninguna regla de negocio,
// endpoint, payload, gate ni estado cambia: los tres bloques operativos son
// los mismos componentes de siempre, y los labels de estado de cada
// acordeón son PRESENTACIONALES, derivados de los estados reales existentes
// (obtenerEstadoCarta / obtenerEstadoEvidencia / obtenerEstadoCartaFirmada /
// puedeFinalizarEvaluacion / usuario_rh). Nada de esto se escribe al backend.
// ════════════════════════════════════════════════════════════════════════════

const TONOS_ESTADO = {
  ok: { background: '#d1fae5', color: '#065f46' },
  pendiente: { background: '#fef3c7', color: '#92400e' },
  aviso: { background: '#fee2e2', color: '#991b1b' },
  progreso: { background: '#dbeafe', color: '#1e40af' },
  neutro: { background: '#f3f4f6', color: '#6b7280' }
};

const ETIQUETA_ESTADO_DOC = { COMPLETA: 'Completa', NINGUNA: 'Pendiente', PARCIAL: 'Inconsistente' };

// Seguimiento RH: "COMPLETADO" = ya existe una revisión RH registrada
// (mismo dato que la propia sección muestra como "Última actualización").
export function estadoSeguimientoRHVista(ev) {
  return ev && ev.usuario_rh
    ? { etiqueta: 'COMPLETADO', tono: 'ok' }
    : { etiqueta: 'PENDIENTE', tono: 'pendiente' };
}

const ETIQUETA_DECISION = { APROBADO: 'Aprobado', NO_APROBADO: 'No aprobado', PENDIENTE: 'Pendiente', NO_APLICA: 'No aplica' };

// Datos mínimos para entender la decisión RH sin abrir la sección.
export function resumenSeguimientoRH(ev) {
  if (!ev || !ev.usuario_rh) return [{ label: 'Estado', valor: 'Sin revisar todavía' }];
  const filas = [];
  const decision = ev.resolucion_rh && ev.resolucion_rh.cambio_salario;
  if (decision) filas.push({ label: 'Cambio de salario', valor: ETIQUETA_DECISION[decision] || decision });
  if (decision === 'APROBADO') {
    const nuevo = fmtSalario(ev.salario_nuevo);
    if (nuevo) filas.push({ label: 'Salario nuevo', valor: nuevo });
    if (ev.fecha_efectiva) filas.push({ label: 'Fecha efectiva', valor: fmtFecha(ev.fecha_efectiva) });
  }
  filas.push({ label: 'Responsable RH', valor: ev.usuario_rh });
  return filas;
}

// Carta de aumento salarial: derivado de obtenerEstadoCarta (BF/BG).
export function estadoCartaVista(ev) {
  const estado = obtenerEstadoCarta(ev);
  if (estado === 'COMPLETA') return { etiqueta: 'COMPLETA', tono: 'ok' };
  if (estado === 'PARCIAL') return { etiqueta: 'REVISAR', tono: 'aviso' };
  return { etiqueta: 'PENDIENTE', tono: 'pendiente' };
}

export function resumenCartaVista(ev) {
  return [
    { label: 'Documento (BF)', valor: ev && ev.documento_aumento_url ? 'Generado' : 'Pendiente' },
    { label: 'PDF (BG)', valor: ev && ev.pdf_aumento_url ? 'Generado' : 'Pendiente' }
  ];
}

// Progreso documental del cierre. NO cuenta "Finalizar" como evidencia:
// total = 1 (evaluación firmada) o 3 (con aumento APROBADO: + Carta generada
// + Carta firmada). Solo presentación; el gate real sigue siendo
// puedeFinalizarEvaluacion.
export function progresoCierre(ev) {
  const items = [{ clave: 'evaluacion_firmada', label: 'Evaluación firmada', estado: obtenerEstadoEvidencia(ev) }];
  if (requiereCartaAumentoFirmada(ev)) {
    items.push({ clave: 'carta_generada', label: 'Carta generada', estado: obtenerEstadoCarta(ev) });
    items.push({ clave: 'carta_firmada', label: 'Carta firmada', estado: obtenerEstadoCartaFirmada(ev) });
  }
  const completados = items.filter(i => i.estado === 'COMPLETA').length;
  const total = items.length;
  const hayParcial = items.some(i => i.estado === 'PARCIAL');
  let etiqueta;
  let tono;
  if (ev && ev.estatus === 'Finalizada') { etiqueta = 'FINALIZADA'; tono = 'ok'; }
  else if (hayParcial) { etiqueta = 'REVISAR'; tono = 'aviso'; }
  else if (completados === total) { etiqueta = 'LISTO PARA FINALIZAR'; tono = 'ok'; }
  else if (completados === 0) { etiqueta = 'PENDIENTE'; tono = 'pendiente'; }
  else { etiqueta = 'EN PROGRESO'; tono = 'progreso'; }
  return { items, completados, total, etiqueta, tono, puedeFinalizar: puedeFinalizarEvaluacion(ev) };
}

// Estado inicial de los acordeones (decisión post-smoke C.2.2): SOLO el
// Resumen abre; respuestas individuales, Seguimiento RH, Carta y Cierre
// arrancan cerrados. Los encabezados ya comunican COMPLETADO / PENDIENTE /
// EN PROGRESO y el usuario decide qué proceso abrir. Estado local de React,
// sin persistencia; el parámetro `ev` se conserva por compatibilidad.
export function estadoInicialAcordeones() {
  return { resumen: true, respuestas: false, seguimiento: false, carta: false, cierre: false };
}

// Dato etiqueta/valor de una evidencia. `truncar` = nombre de archivo: una
// sola línea con elipsis (min-width: 0 + overflow hidden + nowrap) y el valor
// completo en `title`. Sin truncar (usuario, fechas): se permite partir en
// cualquier punto (overflow-wrap: anywhere) -- nunca invade otra columna.
function DatoEvidencia({ label, valor, truncar = false }) {
  const texto = valor || '—';
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase' }}>{label}</div>
      <div
        data-truncar={truncar ? 'true' : undefined}
        title={String(texto)}
        style={truncar
          ? { fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
          : { fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}
      >
        {texto}
      </div>
    </div>
  );
}

// Metadatos de una evidencia (evaluación firmada y Carta firmada comparten
// este patrón): el ARCHIVO ocupa su propia fila a todo lo ancho y los demás
// metadatos van debajo en un grid auto-fit que se apila solo en anchos chicos.
// Nada con posición absoluta ni anchos fijos.
export function MetaEvidencia({ etiquetaArchivo = 'Archivo', archivo, filas }) {
  return (
    <div data-meta-evidencia style={{ marginBottom: '14px', minWidth: 0 }}>
      <div style={{ marginBottom: '10px', minWidth: 0 }}>
        <DatoEvidencia label={etiquetaArchivo} valor={archivo} truncar />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 170px), 1fr))', gap: '12px 16px', minWidth: 0 }}>
        {filas.map(f => <DatoEvidencia key={f.label} label={f.label} valor={f.valor} />)}
      </div>
    </div>
  );
}

// Fila de lista etiqueta (izquierda) / valor (derecha, columna estable).
function FilaLista({ label, valor }) {
  return (
    <div data-fila-lista style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', padding: '7px 0', borderBottom: '1px solid #e8edf4', fontSize: '13px' }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      <span style={{ fontWeight: 700, color: '#1f2937', textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }}>{valor}</span>
    </div>
  );
}

// Contenedor de sección: tarjeta clásica o, cuando va dentro de un acordeón
// (`embebido`), solo el cuerpo -- el encabezado ya lo pone el acordeón.
function CardSeccion({ embebido, titulo, icono, color, style, children }) {
  if (embebido) return <div>{children}</div>;
  return (
    <div className="card" style={style}>
      <div className="card-title" style={{ color }}>
        <i className={icono}></i> {titulo}
      </div>
      {children}
    </div>
  );
}

// Acentos por tono (C.2.4): el color vive SOLO en un borde izquierdo fino,
// el contenedor del icono y el badge -- nunca en la superficie de la card
// (siempre blanca). Paleta del proyecto: --primary #1d3d6b, --secondary
// #0086ab; verde/ámbar/rojo = los mismos de .badge-*.
const ACENTOS_ACORDEON = {
  base: { borde: '#0086ab', iconoBg: '#e6f3f8', icono: '#0086ab' },
  ok: { borde: '#10b981', iconoBg: '#e6f7ef', icono: '#059669' },
  pendiente: { borde: '#f59e0b', iconoBg: '#fff4dc', icono: '#b45309' },
  progreso: { borde: '#6c7be0', iconoBg: '#eaedfc', icono: '#4c5fd5' },
  aviso: { borde: '#ef4444', iconoBg: '#fee2e2', icono: '#b91c1c' }
};

// Estilos de interacción (hover/focus/transiciones cortas). Se inyectan una
// sola vez con el modal; sin dependencias ni animaciones de altura.
const ESTILOS_MODAL = `
.sigrh-acc { transition: box-shadow .15s ease, border-color .15s ease; }
.sigrh-acc:hover { border-color: #cbd8ea; box-shadow: 0 2px 4px rgba(29,61,107,.07), 0 4px 12px rgba(29,61,107,.06); }
.sigrh-acc-head { width: 100%; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 10px 14px; background: transparent; border: none; cursor: pointer; text-align: left; font: inherit; transition: background-color .15s ease; }
.sigrh-acc-head:hover { background-color: #f7faff; }
.sigrh-acc-head:focus-visible { outline: 2px solid var(--secondary); outline-offset: -2px; }
.sigrh-acc-chevron { color: #6b7280; transition: color .15s ease; }
.sigrh-acc-head:hover .sigrh-acc-chevron { color: var(--primary); }
.sigrh-resp-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-template-rows: auto 1fr; gap: 20px 28px; align-items: start; }
.sigrh-resp-r { grid-column: 1; grid-row: 1; min-width: 0; }
.sigrh-resp-a { grid-column: 2; grid-row: 1 / span 2; min-width: 0; }
.sigrh-resp-h { grid-column: 1; grid-row: 2; min-width: 0; }
@media (max-width: 560px) {
  .sigrh-resp-grid { grid-template-columns: minmax(0, 1fr); grid-template-rows: none; }
  .sigrh-resp-r, .sigrh-resp-a, .sigrh-resp-h { grid-column: 1; grid-row: auto; }
}
`;

// Acordeón independiente (estado local). El cuerpo SIEMPRE permanece
// montado y solo se oculta: así el estado interno de SeguimientoRH /
// CartaAumentoSalarial / CierreEvaluacion (formularios a medio llenar,
// archivo seleccionado, subidas en curso) no se pierde al colapsar.
// `variante="sub"` = subacordeón discreto (sin sombra, borde de acento ni
// contenedor de icono).
export function AcordeonOperativo({ id, titulo, icono, subtitulo, estado, tono, resumen, abiertoInicial, variante, children }) {
  const [abierto, setAbierto] = useState(!!abiertoInicial);
  const t = TONOS_ESTADO[tono] || TONOS_ESTADO.neutro;
  const esSub = variante === 'sub';
  const acento = ACENTOS_ACORDEON[tono] || ACENTOS_ACORDEON.base;
  const estiloSeccion = esSub
    ? { border: '1px solid #e5eaf1', borderRadius: '8px', background: '#ffffff', overflow: 'hidden' }
    : {
      border: '1px solid #e3e9f2', borderLeft: `3px solid ${acento.borde}`, borderRadius: '12px', background: '#ffffff',
      marginBottom: '14px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(29,61,107,.06), 0 2px 8px rgba(29,61,107,.04)'
    };
  return (
    <section
      className={esSub ? undefined : 'sigrh-acc'}
      data-acordeon={id}
      data-abierto={abierto ? 'true' : 'false'}
      data-tono={tono || undefined}
      style={estiloSeccion}
    >
      <button
        type="button"
        className="sigrh-acc-head"
        aria-expanded={abierto}
        aria-controls={`acordeon-${id}`}
        onClick={() => setAbierto(a => !a)}
      >
        {icono && !esSub && (
          <span data-icono-acordeon aria-hidden="true" style={{ width: '28px', height: '28px', borderRadius: '8px', background: acento.iconoBg, color: acento.icono, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', flex: '0 0 auto' }}>
            <i className={icono}></i>
          </span>
        )}
        <span style={{ flex: '1 1 200px', minWidth: 0 }}>
          <span data-titulo-acordeon style={esSub
            ? { display: 'block', fontWeight: 600, fontSize: '13px', color: 'var(--secondary)' }
            : { display: 'block', fontWeight: 700, fontSize: '15px', color: 'var(--primary)', letterSpacing: '-0.005em' }}>{titulo}</span>
          {subtitulo && <span style={{ display: 'block', fontSize: '12px', color: '#6b7280' }}>{subtitulo}</span>}
        </span>
        {estado && (
          <span data-estado-acordeon style={{ ...t, fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', padding: '3px 10px', borderRadius: '999px' }}>{estado}</span>
        )}
        <i className={`sigrh-acc-chevron ${abierto ? 'ti ti-chevron-up' : 'ti ti-chevron-down'}`} aria-hidden="true"></i>
        {resumen && (
          <span data-resumen-acordeon style={{ flex: '1 1 100%', display: 'flex', flexWrap: 'wrap', gap: '4px 22px', fontSize: '12px', color: '#4b5563' }}>
            {resumen}
          </span>
        )}
      </button>
      <div id={`acordeon-${id}`} hidden={!abierto} style={{ display: abierto ? undefined : 'none', padding: '4px 14px 14px' }}>
        {children}
      </div>
    </section>
  );
}

function ParResumen({ label, valor }) {
  return (
    <span>
      <span style={{ color: '#9ca3af' }}>{label}: </span>
      <strong style={{ color: '#1f2937', fontWeight: 600 }}>{valor}</strong>
    </span>
  );
}

export function ModalDetalleEvaluacion({ evaluacion: ev, idToken, catalogoRh, onActualizado, onClose }) {
  const etiquetaCampo = { fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' };
  const infoRow = (label, valor) => (
    <div>
      <div style={etiquetaCampo}>{label}</div>
      <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: 500 }}>{valor || '—'}</div>
    </div>
  );

  const salarioAnterior = fmtSalario(ev.salario_anterior);
  const salarioNuevo = fmtSalario(ev.salario_nuevo);
  let porcentajeAumento = null;
  if (ev.salario_anterior && ev.salario_nuevo && Number(ev.salario_anterior) > 0) {
    porcentajeAumento = (((Number(ev.salario_nuevo) - Number(ev.salario_anterior)) / Number(ev.salario_anterior)) * 100).toFixed(2);
  }
  const cambioAprobadoRH = !!(ev.resolucion_rh && ev.resolucion_rh.cambio_salario === 'APROBADO');
  const hayDatosSalario = !!(ev.cambio_salario_control || salarioAnterior || salarioNuevo || ev.fecha_efectiva);

  const respuestaFila = (letra, titulo, val) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: '12px' }}>
      <span style={{ color: '#4b5563' }}>{letra}) {titulo}</span>
      <span style={{ fontWeight: 700, color: '#0086ab' }}>{val ?? '—'}</span>
    </div>
  );

  const inicial = estadoInicialAcordeones(ev);
  const seguimiento = estadoSeguimientoRHVista(ev);
  const mostrarCarta = puedeGenerarCarta(ev);
  const cartaVista = estadoCartaVista(ev);
  const vistaCierre = derivarVistaCierre(ev);
  const mostrarCierre = vistaCierre.modo !== 'OCULTO';
  const cierre = progresoCierre(ev);

  const promedio = (label, valor) => <FilaLista label={label} valor={valor ?? '—'} />;

  return (
    <div className="modal-overlay">
      <div className="modal modal-exp">
        <div className="modal-header" style={{ alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 260px', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0 }}>{ev.nombre}</h2>
              <span style={{ background: '#eef2f7', color: '#374151', border: '1px solid #d1d5db', borderRadius: '999px', padding: '2px 10px', fontSize: '12px', fontWeight: 700 }}>{ev.id_evaluacion}</span>
            </div>
            <div style={{ fontSize: '13px', color: '#4b5563', marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
              <span>#{ev.id_empleado}</span>
              <span>{ev.puesto || '—'}</span>
              <span>{ev.departamento || '—'}</span>
              <span>{ev.planta || '—'}</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span data-estatus className={`badge ${badgeEstado(ev.estatus)}`}>{ev.estatus || 'Sin estado'}</span>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          <style>{ESTILOS_MODAL}</style>

          <div style={{ display: 'flex', gap: '14px', alignItems: 'stretch', flexWrap: 'wrap', marginBottom: '16px' }}>
            <div style={{ flex: '1 1 380px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
              {infoRow('Fecha de ingreso', fmtFecha(ev.fecha_ingreso))}
              {infoRow('Evaluador', ev.nombre_evaluador)}
              {infoRow('Puesto evaluador', ev.puesto_evaluador)}
              {infoRow('Fecha evaluación', fmtFecha(ev.fecha_evaluacion))}
            </div>
            <div data-calificacion style={{ flex: '0 0 auto', display: 'flex', alignItems: 'stretch', background: '#ffffff', border: '1px solid #c7d9f5', borderRadius: '12px', boxShadow: '0 1px 3px rgba(29,61,107,.06), 0 2px 8px rgba(29,61,107,.04)', overflow: 'hidden' }}>
              <div data-col="calificacion" style={{ minWidth: '128px', padding: '10px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                <div style={etiquetaCampo}>Calificación final</div>
                <div style={{ fontSize: '30px', fontWeight: 800, color: 'var(--primary)', lineHeight: 1.1, textAlign: 'center' }}>{ev.calificacion_final ?? '—'}</div>
              </div>
              <div data-col="nivel" style={{ minWidth: '110px', padding: '10px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', borderLeft: '1px solid #dbe4f3' }}>
                <div style={etiquetaCampo}>Nivel</div>
                <div style={{ fontSize: '17px', fontWeight: 700, color: 'var(--secondary)', lineHeight: 1.3, textAlign: 'center' }}>{ev.nivel || '—'}</div>
              </div>
            </div>
          </div>

          <AcordeonOperativo id="resumen" titulo="Resumen de evaluación" icono="ti ti-file-text" abiertoInicial={inicial.resumen}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '18px', marginTop: '6px' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>Promedios por categoría</div>
                <div data-bloque="promedios">
                  {promedio('Responsabilidades', ev.promedio_responsabilidades)}
                  {promedio('Actitudes', ev.promedio_actitudes)}
                  {promedio('Habilidades', ev.promedio_habilidades)}
                </div>
              </div>

              {hayDatosSalario && (
                <div>
                  <div style={{ fontWeight: 700, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>Cambio de salario</div>
                  <div data-bloque="cambio-salario">
                    {/* "Aprobado" SOLO si resolucion_rh.cambio_salario === 'APROBADO';
                        el valor de cambio_salario_control se muestra tal cual. */}
                    <FilaLista label="Cambio de salario" valor={<>
                      {ev.cambio_salario_control || '—'}
                      {cambioAprobadoRH && <span data-decision-rh="aprobado" style={{ marginLeft: '8px', background: '#d1fae5', color: '#065f46', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px' }}>Aprobado</span>}
                    </>} />
                    <FilaLista label="Salario anterior" valor={salarioAnterior || '—'} />
                    <FilaLista label="Salario nuevo" valor={salarioNuevo || '—'} />
                    <FilaLista label="% de aumento" valor={porcentajeAumento !== null ? porcentajeAumento + '%' : '—'} />
                    <FilaLista label="Fecha efectiva" valor={ev.fecha_efectiva ? fmtFecha(ev.fecha_efectiva) : '—'} />
                  </div>
                </div>
              )}

              <div>
                <div style={{ fontWeight: 700, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>Solicitudes del gerente</div>
                {/* Colores NEUTROS/azules a propósito: son solicitudes del
                    gerente, no decisiones de RH (esas viven en Seguimiento RH). */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {Object.keys(SOLICITUD_LABELS).map(k => {
                    const activa = !!(ev.solicitudes && ev.solicitudes[k]);
                    return (
                      <span
                        key={k}
                        data-solicitud={activa ? 'solicitada' : 'no-aplica'}
                        title={activa ? 'Solicitada por el gerente' : 'No solicitada'}
                        style={activa
                          ? { background: '#e8f0fb', color: '#1d3d6b', border: '1px solid #a9c1e6', fontWeight: 600, fontSize: '12px', padding: '4px 11px', borderRadius: '999px' }
                          : { background: '#f3f4f6', color: '#8b95a5', border: '1px solid #e5e7eb', fontSize: '12px', padding: '4px 11px', borderRadius: '999px' }}
                      >
                        {SOLICITUD_LABELS[k]}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            {ev.comentarios && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontWeight: 700, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '6px' }}>Comentarios del evaluador</div>
                <div style={{ fontSize: '13px', color: '#1f2937', background: '#f9fafb', padding: '12px', borderRadius: '8px' }}>{ev.comentarios}</div>
              </div>
            )}

            {ev.respuestas && (
              <div style={{ marginTop: '16px' }}>
                <AcordeonOperativo id="respuestas" variante="sub" titulo="Ver detalle de respuestas individuales" abiertoInicial={inicial.respuestas}>
                  {/* DOM en orden lógico I, II, III. En escritorio la grilla coloca I y III en
                      la columna izquierda y II (que abarca ambas filas) en la derecha;
                      la fila 2 es '1fr', así el alto extra de Actitudes cae DEBAJO de
                      Habilidades y no como hueco entre I y III. Angosto: una columna. */}
                  <div className="sigrh-resp-grid" data-resp-grid style={{ marginTop: '6px' }}>
                    <div className="sigrh-resp-r" data-resp="responsabilidades">
                      <div style={{ fontSize: '12px', fontWeight: 700, color: '#1d3d6b', marginBottom: '4px' }}>I. Responsabilidades</div>
                      {respuestaFila('a', 'Cumplim. cantidades/tiempos', ev.respuestas.responsabilidades?.r1)}
                      {respuestaFila('b', 'Estándares de calidad', ev.respuestas.responsabilidades?.r2)}
                      {respuestaFila('c', 'Uso de recursos', ev.respuestas.responsabilidades?.r3)}
                    </div>
                    <div className="sigrh-resp-a" data-resp="actitudes">
                      <div style={{ fontSize: '12px', fontWeight: 700, color: '#1d3d6b', marginBottom: '4px' }}>II. Actitudes</div>
                      {respuestaFila('a', 'Puntualidad', ev.respuestas.actitudes?.a1)}
                      {respuestaFila('b', 'Orden y limpieza', ev.respuestas.actitudes?.a2)}
                      {respuestaFila('c', 'Actitud de servicio', ev.respuestas.actitudes?.a3)}
                      {respuestaFila('d', 'Iniciativa', ev.respuestas.actitudes?.a4)}
                      {respuestaFila('e', 'Disp. para aprender', ev.respuestas.actitudes?.a5)}
                      {respuestaFila('f', 'Disp. para enseñar', ev.respuestas.actitudes?.a6)}
                      {respuestaFila('g', 'Relaciones de trabajo', ev.respuestas.actitudes?.a7)}
                    </div>
                    <div className="sigrh-resp-h" data-resp="habilidades">
                      <div style={{ fontSize: '12px', fontWeight: 700, color: '#1d3d6b', marginBottom: '4px' }}>III. Habilidades</div>
                      {respuestaFila('a', 'Aprovech. capacitación', ev.respuestas.habilidades?.h1)}
                      {respuestaFila('b', 'Manejo de equipo', ev.respuestas.habilidades?.h2)}
                      {respuestaFila('c', 'Comunicación', ev.respuestas.habilidades?.h3)}
                    </div>
                  </div>
                </AcordeonOperativo>
              </div>
            )}
          </AcordeonOperativo>

          <AcordeonOperativo
            id="seguimiento"
            titulo="Seguimiento RH"
            icono="ti ti-progress"
            estado={seguimiento.etiqueta}
            tono={seguimiento.tono}
            abiertoInicial={inicial.seguimiento}
            resumen={resumenSeguimientoRH(ev).map(f => <ParResumen key={f.label} label={f.label} valor={f.valor} />)}
          >
            <SeguimientoRH ev={ev} idToken={idToken} onActualizado={onActualizado} embebido />
          </AcordeonOperativo>

          {mostrarCarta && (
            <AcordeonOperativo
              id="carta"
              titulo="Carta de aumento salarial"
              icono="ti ti-file-certificate"
              estado={cartaVista.etiqueta}
              tono={cartaVista.tono}
              abiertoInicial={inicial.carta}
              resumen={resumenCartaVista(ev).map(f => <ParResumen key={f.label} label={f.label} valor={f.valor} />)}
            >
              <CartaAumentoSalarial ev={ev} idToken={idToken} catalogoRh={catalogoRh} onActualizado={onActualizado} embebido />
            </AcordeonOperativo>
          )}

          {mostrarCierre && (
            <AcordeonOperativo
              id="cierre"
              titulo="Cierre de evaluación"
              icono="ti ti-paperclip"
              estado={cierre.etiqueta}
              tono={cierre.tono}
              abiertoInicial={inicial.cierre}
              resumen={[
                <ParResumen key="prog" label="Requisitos documentales" valor={`${cierre.completados}/${cierre.total} completos`} />,
                ...cierre.items.map(i => <ParResumen key={i.clave} label={i.label} valor={ETIQUETA_ESTADO_DOC[i.estado]} />),
                ev.estatus === 'Finalizada' ? null : <ParResumen key="fin" label="Finalizar" valor={cierre.puedeFinalizar ? 'Disponible' : 'No disponible'} />
              ]}
            >
              <CierreEvaluacion ev={ev} idToken={idToken} onActualizado={onActualizado} embebido />
            </AcordeonOperativo>
          )}

        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SEGUIMIENTO RH — resolución editable por solicitud, sin autosave
// ════════════════════════════════════════════════════════════════════════════
function SeguimientoRH({ ev, idToken, onActualizado, embebido = false }) {
  const activas = solicitudesActivas(ev);
  const resolucionInicial = ev.resolucion_rh || {};

  const [resolucion, setResolucion] = useState(() => {
    const base = {};
    activas.forEach(k => { base[k] = resolucionInicial[k] === 'APROBADO' || resolucionInicial[k] === 'NO_APROBADO' ? resolucionInicial[k] : 'PENDIENTE'; });
    return base;
  });
  const [observaciones, setObservaciones] = useState(ev.observaciones_rh || '');
  const [salarioActual, setSalarioActual] = useState(fmtSalarioInput(ev.salario_anterior));
  const [salarioConfirmado, setSalarioConfirmado] = useState(!!ev.salario_anterior);
  const [estadoSugerencia, setEstadoSugerencia] = useState(null); // 'cargando' | 'ok' | 'no-disponible' | null
  const [salarioNuevo, setSalarioNuevo] = useState(fmtSalarioInput(ev.salario_nuevo));
  const [fechaEfectiva, setFechaEfectiva] = useState(ev.fecha_efectiva || '');
  const [guardando, setGuardando] = useState(false);
  const [errorGuardado, setErrorGuardado] = useState('');
  const [exito, setExito] = useState(false);
  // Ref además del state: el state de React se re-renderiza de forma
  // asíncrona/batched, dejando una ventana breve donde un doble clic real
  // podría disparar dos guardados antes de que el botón se vea deshabilitado.
  // El ref bloquea sincrónicamente en el primer clic.
  const guardandoRef = useRef(false);

  const cambioSalarioActivo = !!(ev.solicitudes && ev.solicitudes.cambio_salario);
  const cambioSalarioAprobado = resolucion.cambio_salario === 'APROBADO';

  useEffect(() => {
    if (!cambioSalarioActivo || ev.salario_anterior) return;
    let cancelado = false;
    setEstadoSugerencia('cargando');
    solicitarSalarioSugerido(idToken, ev.id_empleado)
      .then(data => {
        if (cancelado) return;
        if (data.success && data.salario_diario_sugerido !== null && data.salario_diario_sugerido !== undefined) {
          setSalarioActual(fmtSalarioInput(data.salario_diario_sugerido));
          setEstadoSugerencia('ok');
        } else {
          setEstadoSugerencia('no-disponible');
        }
      })
      .catch(() => { if (!cancelado) setEstadoSugerencia('no-disponible'); });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cambioSalarioActivo]);

  const salarioActualNum = parseFloat(salarioActual);
  const salarioNuevoNum = parseFloat(salarioNuevo);
  const porcentaje = calcularPorcentajeAumento(salarioActualNum, salarioNuevoNum);

  const fechaEsJueves = fechaEfectiva ? esJuevesISO(fechaEfectiva) : false;

  const faltaDatoAumento = faltanDatosAumento({ cambioSalarioAprobado, salarioConfirmado, salarioActualNum, salarioNuevoNum, fechaEfectiva });

  const puedeGuardar = !guardando && !faltaDatoAumento;

  const handleGuardar = async () => {
    if (!puedeIntentarGuardar(guardando, puedeGuardar) || guardandoRef.current) return;
    guardandoRef.current = true;
    setGuardando(true);
    setErrorGuardado('');
    setExito(false);
    try {
      const payload = construirPayloadSeguimiento({
        idEvaluacion: ev.id_evaluacion, resolucion, observaciones,
        cambioSalarioAprobado, salarioActualNum, salarioNuevoNum, fechaEfectiva
      });
      const data = await ejecutarGuardarSeguimiento(idToken, payload, fetch);
      if (data.success) {
        setExito(true);
        onActualizado(data.evaluacion);
      } else {
        setErrorGuardado(data.error || 'No fue posible guardar el seguimiento.');
      }
    } catch {
      setErrorGuardado('No se pudo conectar con el servidor.');
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  return (
    <CardSeccion embebido={embebido} titulo="Seguimiento RH" icono="ti ti-progress" color="#1d3d6b" style={{ background: '#f8faff', borderColor: '#c7d9f5', marginBottom: '20px' }}>

      {activas.length === 0 && (
        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
          El gerente no realizó ninguna solicitud en esta evaluación. Al guardar, esta evaluación queda registrada como revisada por RH.
        </div>
      )}

      {activas.map(key => {
        const cls = RESOLUCION_BADGE[resolucion[key]];
        return (
          <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>{SOLICITUD_LABELS[key]}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className={`badge ${cls || ''}`} style={!cls ? { background: '#f3f4f6', color: '#9ca3af' } : undefined}>{resolucion[key]}</span>
              <select
                value={resolucion[key]}
                onChange={e => setResolucion(prev => ({ ...prev, [key]: e.target.value }))}
                style={{ fontSize: '12px' }}
              >
                {RESOLUCIONES_SELECCIONABLES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>
        );
      })}

      {cambioSalarioActivo && cambioSalarioAprobado && (
        <div style={{ marginTop: '16px', padding: '14px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <div style={{ fontWeight: 700, fontSize: '12px', color: '#1d3d6b', textTransform: 'uppercase', marginBottom: '10px' }}>Cambio de salario</div>

          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label>Salario diario actual {estadoSugerencia === 'cargando' ? '(buscando sugerencia...)' : ''}</label>
            <input
              type="number" step="0.01" min="0" value={salarioActual}
              onChange={e => { setSalarioActual(e.target.value); setSalarioConfirmado(false); }}
              placeholder="0.00"
            />
            {estadoSugerencia === 'no-disponible' && !ev.salario_anterior && (
              <div style={{ fontSize: '11px', color: '#d97706', marginTop: '4px' }}>No se encontró un salario sugerido en el expediente del empleado — captúralo manualmente.</div>
            )}
            {!salarioConfirmado && salarioActual && (
              <button type="button" className="btn-secondary" style={{ marginTop: '6px', fontSize: '11px', padding: '6px 10px', alignSelf: 'flex-start' }} onClick={() => setSalarioConfirmado(true)}>
                Confirmar salario actual
              </button>
            )}
            {salarioConfirmado && (
              <div style={{ fontSize: '11px', color: '#059669', marginTop: '4px' }}>✓ Confirmado — se guardará como salario anterior de este proceso.</div>
            )}
          </div>

          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label>Salario diario nuevo</label>
            <input type="number" step="0.01" min="0" value={salarioNuevo} onChange={e => setSalarioNuevo(e.target.value)} placeholder="352.52" />
          </div>

          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label>Porcentaje de incremento</label>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#0086ab' }}>{porcentaje !== null ? `${porcentaje}%` : '—'}</div>
          </div>

          <div className="form-group">
            <label>Fecha efectiva (debe ser jueves)</label>
            <input type="date" value={fechaEfectiva} onChange={e => setFechaEfectiva(e.target.value)} />
            {fechaEfectiva && !fechaEsJueves && (
              <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px' }}>El aumento siempre entra en vigor en jueves — elige una fecha que caiga en jueves.</div>
            )}
          </div>
        </div>
      )}

      <div className="form-group" style={{ marginTop: '14px' }}>
        <label>Observaciones RH</label>
        <textarea
          value={observaciones}
          onChange={e => setObservaciones(e.target.value)}
          rows={2}
          style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', fontFamily: 'inherit', resize: 'vertical' }}
        />
      </div>

      {errorGuardado && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '8px 12px', marginTop: '10px', fontSize: '12px', color: '#dc2626' }}>{errorGuardado}</div>
      )}
      {exito && !errorGuardado && (
        <div style={{ fontSize: '12px', color: '#059669', marginTop: '10px' }}>✓ Seguimiento guardado.</div>
      )}

      <div style={{ marginTop: '14px' }}>
        <button className="btn-primary" disabled={!puedeGuardar} onClick={handleGuardar}>
          {guardando ? 'Guardando...' : 'Guardar seguimiento'}
        </button>
      </div>

      <div style={{ marginTop: '10px', fontSize: '11px', color: '#9ca3af' }}>
        Última actualización: {ev.usuario_rh ? `${ev.usuario_rh} — ${fmtFecha(ev.fecha_actualizacion_rh)}` : 'sin revisar todavía'}
      </div>
    </CardSeccion>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// CARTA DE AUMENTO SALARIAL — acción completamente independiente de
// Seguimiento RH (nunca comparte lógica ni handler con "Guardar
// seguimiento"). Solo se monta cuando puedeGenerarCarta(ev) es true, ya
// evaluado por el componente padre a partir del `ev` confirmado por
// servidor.
// ════════════════════════════════════════════════════════════════════════════
function CartaAumentoSalarial({ ev, idToken, catalogoRh, onActualizado, embebido = false }) {
  const [incluirMensual, setIncluirMensual] = useState(false);
  const [salarioMensualActual, setSalarioMensualActual] = useState('');
  const [salarioMensualNuevo, setSalarioMensualNuevo] = useState('');
  const [generando, setGenerando] = useState(false);
  const [errorGeneracion, setErrorGeneracion] = useState('');
  // Mismo motivo que guardandoRef en SeguimientoRH: bloqueo síncrono de
  // doble clic, independiente de la ventana de re-render de React.
  const generandoRef = useRef(false);

  // ── Corrección de RH de evaluación ──
  // corrigiendoRh: el usuario pulsó explícitamente "Corregir RH" (solo
  // relevante cuando YA hay un RH efectivo -- si no hay ninguno, el
  // selector se muestra directamente, sin este paso).
  const [corrigiendoRh, setCorrigiendoRh] = useState(false);
  const [rhSeleccionadoTemp, setRhSeleccionadoTemp] = useState('');
  const [confirmandoCambioRh, setConfirmandoCambioRh] = useState(false);
  const [guardandoRh, setGuardandoRh] = useState(false);
  const [errorRh, setErrorRh] = useState('');
  const guardandoRhRef = useRef(false);

  const salarioMensualActualNum = parseFloat(salarioMensualActual);
  const salarioMensualNuevoNum = parseFloat(salarioMensualNuevo);
  const faltanMensuales = faltanSalariosMensuales(incluirMensual, salarioMensualActualNum, salarioMensualNuevoNum);
  const rhEfectivoPresente = tieneRhEfectivo(ev);
  // Generar carta exige, además de los salarios mensuales completos, que
  // exista un RH efectivo -- mismo gate que el backend, aquí solo para no
  // ofrecer un botón que el servidor de todos modos rechazaría.
  const puedeGenerarAhora = !generando && !faltanMensuales && rhEfectivoPresente;

  const estadoCarta = obtenerEstadoCarta(ev);
  const mostrarSelectorRh = !rhEfectivoPresente || corrigiendoRh;
  const catalogoRhSeguro = catalogoRh || [];

  const handleGuardarRhClick = () => {
    // Si ya hay un RH efectivo, esto es una CORRECCIÓN real -- exige
    // confirmación explícita antes de guardar (nunca reemplaza en
    // silencio). Si no hay ninguno, es solo completar un campo vacío --
    // se guarda directo.
    if (rhEfectivoPresente) {
      setConfirmandoCambioRh(true);
    } else {
      handleGuardarRh();
    }
  };

  const handleGuardarRh = async () => {
    if (!rhSeleccionadoTemp || guardandoRh || guardandoRhRef.current) return;
    guardandoRhRef.current = true;
    setGuardandoRh(true);
    setErrorRh('');
    try {
      const payload = construirPayloadCorregirRh({ idEvaluacion: ev.id_evaluacion, rhSeleccionado: rhSeleccionadoTemp });
      const data = await ejecutarCorregirRhEvaluacion(idToken, payload, fetch);
      if (data.success) {
        // Estado local se actualiza con la respuesta REAL del servidor
        // (nombre canónico, usuario y fecha reales), no con lo que el
        // usuario tecleó/eligió -- nunca optimistic UI puro.
        onActualizado({
          ...ev,
          rh_evaluacion_corregido: data.rh_efectivo,
          rh_efectivo: data.rh_efectivo,
          usuario_correccion_rh_evaluacion: data.usuario_correccion,
          fecha_correccion_rh_evaluacion: data.fecha_correccion
        });
        setCorrigiendoRh(false);
        setConfirmandoCambioRh(false);
        setRhSeleccionadoTemp('');
      } else {
        setErrorRh(data.error || 'No fue posible guardar la corrección.');
      }
    } catch {
      setErrorRh('No se pudo conectar con el servidor.');
    } finally {
      guardandoRhRef.current = false;
      setGuardandoRh(false);
    }
  };

  const salarioAnteriorFmt = fmtSalario(ev.salario_anterior);
  const salarioNuevoFmt = fmtSalario(ev.salario_nuevo);
  let porcentaje = null;
  if (ev.salario_anterior && ev.salario_nuevo && Number(ev.salario_anterior) > 0) {
    porcentaje = (((Number(ev.salario_nuevo) - Number(ev.salario_anterior)) / Number(ev.salario_anterior)) * 100).toFixed(2);
  }

  const handleGenerar = async () => {
    if (!puedeIntentarGuardar(generando, puedeGenerarAhora) || generandoRef.current) return;
    generandoRef.current = true;
    setGenerando(true);
    setErrorGeneracion('');
    try {
      const payload = construirPayloadCartaAumento({
        idEvaluacion: ev.id_evaluacion, incluirMensual, salarioMensualActualNum, salarioMensualNuevoNum
      });
      const data = await ejecutarGenerarCartaAumento(idToken, payload, fetch);
      if (data.success) {
        // No se pierden los campos ya conocidos de `ev` -- solo se
        // actualizan los 5 campos de trazabilidad que el servidor acaba de
        // confirmar.
        onActualizado({
          ...ev,
          documento_aumento_url: data.documento_url,
          pdf_aumento_url: data.pdf_url,
          fecha_generacion_documento: data.fecha_generacion,
          usuario_generacion_documento: data.usuario_generacion,
          variante_documento: data.variante
        });
      } else {
        // Los inputs mensuales capturados NO se limpian ante un error --
        // simplemente no se llama a ningún setState que los toque.
        setErrorGeneracion(data.error || 'No fue posible crear la propuesta.');
      }
    } catch {
      setErrorGeneracion('No se pudo conectar con el servidor.');
    } finally {
      generandoRef.current = false;
      setGenerando(false);
    }
  };

  return (
    <CardSeccion embebido={embebido} titulo="Carta de aumento salarial" icono="ti ti-file-certificate" color="#92400e" style={{ background: '#fdf8f0', borderColor: '#f3d9a4', marginBottom: '20px' }}>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
        <div>
          <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase' }}>Salario diario actual</div>
          <div style={{ fontWeight: 600 }}>{salarioAnteriorFmt || '—'}</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase' }}>Salario diario nuevo</div>
          <div style={{ fontWeight: 600 }}>{salarioNuevoFmt || '—'}</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase' }}>Porcentaje</div>
          <div style={{ fontWeight: 600 }}>{porcentaje !== null ? `${porcentaje}%` : '—'}</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase' }}>Fecha efectiva</div>
          <div style={{ fontWeight: 600 }}>{ev.fecha_efectiva ? fmtFecha(ev.fecha_efectiva) : '—'}</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase' }}>Evaluador</div>
          <div style={{ fontWeight: 600 }}>{ev.nombre_evaluador || '—'}</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase' }}>RH original</div>
          <div style={{ fontWeight: 600 }}>{ev.nombre_rh_original || '—'}</div>
        </div>
      </div>

      {/* ── RH responsable de la evaluación (RH efectivo) ── */}
      <div style={{ marginBottom: '16px', padding: '12px 14px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
        <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>RH responsable de la evaluación</div>

        {!mostrarSelectorRh && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ fontWeight: 600 }}>{ev.rh_efectivo}</div>
            <button type="button" className="btn-secondary" style={{ fontSize: '11px', padding: '4px 10px' }} onClick={() => { setCorrigiendoRh(true); setRhSeleccionadoTemp(''); setErrorRh(''); }}>
              Corregir RH
            </button>
          </div>
        )}

        {mostrarSelectorRh && !confirmandoCambioRh && (
          <>
            <select
              value={rhSeleccionadoTemp}
              onChange={e => setRhSeleccionadoTemp(e.target.value)}
              style={{ width: '100%', marginBottom: '8px' }}
            >
              <option value="">-- Selecciona --</option>
              {catalogoRhSeguro.map(nombre => <option key={nombre} value={nombre}>{nombre}</option>)}
            </select>

            {errorRh && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '6px 10px', marginBottom: '8px', fontSize: '12px', color: '#dc2626' }}>{errorRh}</div>
            )}

            {!rhEfectivoPresente && (
              <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: '8px' }}>Selecciona el RH responsable de la evaluación antes de generar la carta.</div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" className="btn-primary" disabled={!rhSeleccionadoTemp || guardandoRh} onClick={handleGuardarRhClick}>
                {guardandoRh ? 'Guardando...' : 'Guardar RH'}
              </button>
              {corrigiendoRh && (
                <button type="button" className="btn-secondary" onClick={() => { setCorrigiendoRh(false); setRhSeleccionadoTemp(''); setErrorRh(''); }}>
                  Cancelar
                </button>
              )}
            </div>
          </>
        )}

        {confirmandoCambioRh && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 12px' }}>
            <div style={{ fontSize: '13px', marginBottom: '10px' }}>
              ¿Confirmas cambiar el RH responsable de <strong>{ev.rh_efectivo}</strong> a <strong>{rhSeleccionadoTemp}</strong>?
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" className="btn-primary" disabled={guardandoRh} onClick={handleGuardarRh}>
                {guardandoRh ? 'Guardando...' : 'Confirmar cambio'}
              </button>
              <button type="button" className="btn-secondary" disabled={guardandoRh} onClick={() => setConfirmandoCambioRh(false)}>
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      {estadoCarta === 'COMPLETA' && (
        <>
          <div style={{ fontSize: '12px', color: '#059669', marginBottom: '10px' }}>
            ✓ Ya existe una propuesta generada para esta evaluación ({ev.variante_documento === 'DIARIO_MENSUAL' ? 'diario + mensual' : 'diario'}).
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <a className="btn-secondary" href={ev.documento_aumento_url} target="_blank" rel="noreferrer">Abrir documento</a>
            <a className="btn-secondary" href={ev.pdf_aumento_url} target="_blank" rel="noreferrer">Abrir PDF</a>
          </div>
        </>
      )}

      {estadoCarta === 'PARCIAL' && (
        // Estado inconsistente (mismo criterio que el backend, sección 18):
        // NO se ofrece "Generar carta de aumento" -- eso reintentaría sobre
        // un estado a medias. NO se presenta como completa. Se muestra
        // únicamente el enlace que sí existe, por si sirve de diagnóstico
        // para RH, nunca los dos como si la propuesta estuviera terminada.
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 14px' }}>
          <div style={{ fontSize: '12px', color: '#dc2626', fontWeight: 600, marginBottom: ev.documento_aumento_url || ev.pdf_aumento_url ? '8px' : 0 }}>
            ⚠ La propuesta existente está incompleta y requiere revisión de RH.
          </div>
          {(ev.documento_aumento_url || ev.pdf_aumento_url) && (
            <div style={{ display: 'flex', gap: '10px' }}>
              {ev.documento_aumento_url && (
                <a className="btn-secondary" href={ev.documento_aumento_url} target="_blank" rel="noreferrer">Abrir documento (parcial)</a>
              )}
              {ev.pdf_aumento_url && (
                <a className="btn-secondary" href={ev.pdf_aumento_url} target="_blank" rel="noreferrer">Abrir PDF (parcial)</a>
              )}
            </div>
          )}
        </div>
      )}

      {estadoCarta === 'NINGUNA' && (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '12px', cursor: 'pointer' }}>
            <input type="checkbox" checked={incluirMensual} onChange={e => setIncluirMensual(e.target.checked)} />
            Incluir salario mensual en la propuesta
          </label>

          {incluirMensual && (
            <div className="form-grid" style={{ marginBottom: '12px' }}>
              <div className="form-group">
                <label>Salario mensual actual</label>
                <input type="number" step="0.01" min="0" value={salarioMensualActual} onChange={e => setSalarioMensualActual(e.target.value)} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label>Salario mensual nuevo</label>
                <input type="number" step="0.01" min="0" value={salarioMensualNuevo} onChange={e => setSalarioMensualNuevo(e.target.value)} placeholder="0.00" />
              </div>
            </div>
          )}

          {errorGeneracion && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '8px 12px', marginBottom: '10px', fontSize: '12px', color: '#dc2626' }}>{errorGeneracion}</div>
          )}

          <button className="btn-primary" disabled={!puedeGenerarAhora} onClick={handleGenerar}>
            {generando ? 'Generando...' : 'Generar carta de aumento'}
          </button>
        </>
      )}
    </CardSeccion>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// CIERRE DE EVALUACIÓN (Bloque B) — evidencia firmada + finalizar. Acción
// completamente independiente de Seguimiento RH y Carta de Aumento (nunca
// comparte handler ni estado con ellas). Alcance de esta fase: visualizar
// estado de cierre, adjuntar evidencia, visualizar evidencia existente y
// finalizar -- NO reemplazo de evidencia, NO reapertura, NO eliminación.
//
// Tras cualquier subida/cierre, este componente NUNCA confía en el estado
// local: siempre refresca el listado completo desde servidor
// (consultarEvaluaciones) y localiza la evaluación de forma inequívoca
// (localizarEvaluacionUnica -- nunca "la primera coincidencia" por ID,
// justamente por los duplicados históricos EVA-0003/EVA-0004) antes de
// actualizar la UI. Ver ejecutarFlujoSubirEvidencia/
// ejecutarFlujoFinalizarEvaluacion arriba para la lógica de decisión,
// probada de forma independiente en Evaluaciones.test.js.
// ════════════════════════════════════════════════════════════════════════════
function CierreEvaluacion({ ev, idToken, onActualizado, embebido = false }) {
  const [archivo, setArchivo] = useState(null);
  const [errorArchivo, setErrorArchivo] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [faseSubida, setFaseSubida] = useState(''); // '' | 'verificando'
  const [errorSubida, setErrorSubida] = useState('');
  // Mismo motivo que guardandoRef/generandoRef en los bloques de arriba:
  // bloqueo síncrono de doble clic, independiente de la ventana de
  // re-render de React (sección 8: nunca una segunda petición mientras la
  // primera sigue pendiente).
  const subiendoRef = useRef(false);

  const [confirmandoCierre, setConfirmandoCierre] = useState(false);
  const [finalizando, setFinalizando] = useState(false);
  const [faseFinalizar, setFaseFinalizar] = useState('');
  const [errorFinalizar, setErrorFinalizar] = useState('');
  const finalizandoRef = useRef(false);

  // Carta de aumento firmada: estado completamente independiente del de la
  // evaluación firmada (archivo, error, bandera de subida y ref propios).
  const [archivoCartaFirmada, setArchivoCartaFirmada] = useState(null);
  const [errorArchivoCartaFirmada, setErrorArchivoCartaFirmada] = useState('');
  const [subiendoCartaFirmada, setSubiendoCartaFirmada] = useState(false);
  const [faseCartaFirmada, setFaseCartaFirmada] = useState('');
  const [errorSubidaCartaFirmada, setErrorSubidaCartaFirmada] = useState('');
  const subiendoCartaFirmadaRef = useRef(false);

  const estadoEvidencia = obtenerEstadoEvidencia(ev);
  const esPendienteFirma = ev.estatus === 'Pendiente 1:1 / Firma';
  const esFinalizada = ev.estatus === 'Finalizada';
  const vista = derivarVistaCierre(ev);

  const handleSeleccionarCartaFirmada = (file) => {
    setErrorSubidaCartaFirmada('');
    if (!file) { setArchivoCartaFirmada(null); setErrorArchivoCartaFirmada(''); return; }
    const validacion = validarArchivoEvidencia(file.type, file.size);
    if (!validacion.ok) {
      setArchivoCartaFirmada(null);
      setErrorArchivoCartaFirmada(validacion.error);
      return;
    }
    setArchivoCartaFirmada(file);
    setErrorArchivoCartaFirmada('');
  };

  const handleAdjuntarCartaFirmada = async () => {
    if (subiendoCartaFirmada || subiendoCartaFirmadaRef.current || !archivoCartaFirmada) return;
    subiendoCartaFirmadaRef.current = true;
    setSubiendoCartaFirmada(true);
    setFaseCartaFirmada('');
    setErrorSubidaCartaFirmada('');
    try {
      const dataUrl = await fileToBase64(archivoCartaFirmada);
      const base64 = extraerBase64Puro(dataUrl);
      const payload = construirPayloadSubirCartaFirmada({
        idEvaluacion: ev.id_evaluacion,
        archivoBase64: base64,
        archivoMime: archivoCartaFirmada.type,
        archivoNombreOriginal: archivoCartaFirmada.name
      });
      const resultado = await ejecutarFlujoSubirCartaFirmada(idToken, payload, ev.id_evaluacion, fetch, setFaseCartaFirmada);
      if (resultado.resultado === 'EXITO') {
        onActualizado(resultado.evaluacion);
        setArchivoCartaFirmada(null); // solo tras éxito confirmado
      } else if (resultado.resultado === 'INTEGRIDAD') {
        if (resultado.evaluacion) onActualizado(resultado.evaluacion);
        setErrorSubidaCartaFirmada(resultado.error);
      } else {
        // Fallo confirmado/no confirmado: se conserva el archivo para reintento manual.
        setErrorSubidaCartaFirmada(resultado.error);
      }
    } catch {
      setErrorSubidaCartaFirmada('No se pudo leer el archivo seleccionado. Intenta nuevamente.');
    } finally {
      subiendoCartaFirmadaRef.current = false;
      setSubiendoCartaFirmada(false);
      setFaseCartaFirmada('');
    }
  };

  const handleSeleccionarArchivo = (file) => {
    setErrorSubida('');
    if (!file) { setArchivo(null); setErrorArchivo(''); return; }
    const validacion = validarArchivoEvidencia(file.type, file.size);
    if (!validacion.ok) {
      setArchivo(null);
      setErrorArchivo(validacion.error);
      return;
    }
    setArchivo(file);
    setErrorArchivo('');
  };

  const handleAdjuntar = async () => {
    if (subiendo || subiendoRef.current || !archivo) return;
    subiendoRef.current = true;
    setSubiendo(true);
    setFaseSubida('');
    setErrorSubida('');
    try {
      const dataUrl = await fileToBase64(archivo);
      const base64 = extraerBase64Puro(dataUrl);
      const payload = construirPayloadSubirEvidencia({
        idEvaluacion: ev.id_evaluacion,
        archivoBase64: base64,
        archivoMime: archivo.type,
        archivoNombreOriginal: archivo.name
      });
      const resultado = await ejecutarFlujoSubirEvidencia(idToken, payload, ev.id_evaluacion, fetch, setFaseSubida);
      if (resultado.resultado === 'EXITO') {
        onActualizado(resultado.evaluacion);
        setArchivo(null);
      } else if (resultado.resultado === 'INTEGRIDAD') {
        // Se refleja el estado PARCIAL confirmado por servidor en el `ev`
        // del padre -- el siguiente render de este mismo componente ya
        // entra por la rama de bloqueo de integridad, sin necesidad de un
        // flag local aparte.
        if (resultado.evaluacion) onActualizado(resultado.evaluacion);
        setErrorSubida(resultado.error);
      } else {
        setErrorSubida(resultado.error);
      }
    } catch {
      setErrorSubida('No se pudo leer el archivo seleccionado. Intenta nuevamente.');
    } finally {
      subiendoRef.current = false;
      setSubiendo(false);
      setFaseSubida('');
    }
  };

  const handleFinalizar = async () => {
    if (finalizando || finalizandoRef.current) return;
    finalizandoRef.current = true;
    setFinalizando(true);
    setFaseFinalizar('');
    setErrorFinalizar('');
    try {
      const resultado = await ejecutarFlujoFinalizarEvaluacion(idToken, ev.id_evaluacion, fetch, setFaseFinalizar);
      if (resultado.resultado === 'EXITO') {
        onActualizado(resultado.evaluacion);
        setConfirmandoCierre(false);
      } else {
        setErrorFinalizar(resultado.error);
        setConfirmandoCierre(false);
      }
    } finally {
      finalizandoRef.current = false;
      setFinalizando(false);
      setFaseFinalizar('');
    }
  };

  // ── Visibilidad (sección 2) ──
  if (!esPendienteFirma && !esFinalizada) return null;
  // Finalizada histórica (BN:BT vacíos): nunca error, nunca "falta
  // evidencia" -- es una evaluación válida anterior a este bloque. No se
  // muestra sección de cierre en absoluto (sección 18).
  if (esFinalizada && estadoEvidencia === 'NINGUNA') return null;

  const boxStyle = { background: '#f8faff', borderColor: '#c7d9f5', marginBottom: '20px' };
  const errorBoxStyle = { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: '#dc2626' };

  // ── PARCIAL: error de integridad, bloquea adjuntar y finalizar
  // (sección 3) -- aplica tanto en Pendiente 1:1/Firma como en Finalizada
  // (esta última sería una anomalía real, no una histórica válida). ──
  if (esFinalizada && estadoEvidencia === 'PARCIAL') {
    return (
      <CardSeccion embebido={embebido} titulo="Cierre de evaluación" icono="ti ti-file-certificate" color="#1d3d6b" style={boxStyle}>
        <div style={errorBoxStyle}>
          ⚠ La evidencia de esta evaluación quedó en un estado inconsistente (solo algunos de los campos esperados están presentes). Contacta a TI antes de continuar — no se puede adjuntar ni finalizar hasta resolverlo.
        </div>
      </CardSeccion>
    );
  }

  // ── Finalizada + evidencia COMPLETA: solo lectura (sección 17) --
  // nunca botones de reemplazar/subir/finalizar. ──
  if (esFinalizada && estadoEvidencia === 'COMPLETA') {
    return (
      <CardSeccion embebido={embebido} titulo="Evaluación finalizada" icono="ti ti-file-certificate" color="#1d3d6b" style={boxStyle}>
        <MetaEvidencia etiquetaArchivo="Evidencia" archivo={ev.nombre_archivo_evidencia} filas={[{ label: 'Adjuntada por', valor: ev.usuario_subida_evidencia }, { label: 'Fecha de evidencia', valor: fmtFecha(ev.fecha_subida_evidencia) }, { label: 'Finalizada por', valor: ev.usuario_cierre_evaluacion }, { label: 'Fecha de cierre', valor: ev.fecha_cierre_evaluacion ? fmtFecha(ev.fecha_cierre_evaluacion) : '' }]} />
        <a className="btn-secondary" href={ev.evidencia_firma_url} target="_blank" rel="noreferrer">Ver evidencia</a>

        {vista.integridadCartaFirmada && (
          <div style={{ ...errorBoxStyle, marginTop: '14px' }}>
            ⚠ La carta de aumento firmada de esta evaluación quedó en un estado inconsistente. Contacta a TI.
          </div>
        )}
        {vista.mostrarCartaFirmada && (
          <div style={{ marginTop: '18px' }}>
            <div style={{ fontWeight: 700, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '10px' }}>Carta de aumento firmada</div>
            <MetaEvidencia archivo={ev.nombre_archivo_carta_firmada} filas={[{ label: 'Adjuntada por', valor: ev.usuario_subida_carta_firmada }, { label: 'Fecha', valor: fmtFecha(ev.fecha_subida_carta_firmada) }]} />
            <a className="btn-secondary" href={ev.evidencia_carta_firmada_url} target="_blank" rel="noreferrer">Ver carta firmada</a>
          </div>
        )}
      </CardSeccion>
    );
  }

  // A partir de aquí la única rama restante es esPendienteFirma === true
  // (Finalizada+NINGUNA y Finalizada+COMPLETA ya retornaron arriba, y
  // PARCIAL también, sin importar el estatus).
  return (
    <CardSeccion embebido={embebido} titulo="Cierre de evaluación" icono="ti ti-file-certificate" color="#1d3d6b" style={boxStyle}>

      {estadoEvidencia === 'NINGUNA' && (
        <>
          <div style={{ fontSize: '13px', color: '#4b5563', marginBottom: '12px' }}>
            Adjunta la evaluación firmada después de realizar la retroalimentación 1:1 con el colaborador.
          </div>
          <div className="form-group" style={{ marginBottom: '10px' }}>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              disabled={subiendo}
              onChange={e => handleSeleccionarArchivo(e.target.files[0] || null)}
            />
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>PDF, JPG o PNG · Máx. 5 MB</div>
            {archivo && !errorArchivo && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: '#1d3d6b' }}>
                📎 {archivo.name} ({(archivo.size / 1024).toFixed(1)} KB)
              </div>
            )}
          </div>

          {errorArchivo && <div style={{ ...errorBoxStyle, marginBottom: '10px' }}>{errorArchivo}</div>}

          {subiendo && faseSubida === 'verificando' && (
            <div style={{ fontSize: '12px', color: '#d97706', marginBottom: '10px' }}>No se pudo confirmar el resultado. Verificando estado...</div>
          )}
          {errorSubida && <div style={{ ...errorBoxStyle, marginBottom: '10px' }}>{errorSubida}</div>}

          <button className="btn-primary" disabled={!archivo || !!errorArchivo || subiendo} onClick={handleAdjuntar}>
            {subiendo ? 'Adjuntando evidencia...' : 'Adjuntar evidencia'}
          </button>

        </>
      )}

      {vista.bloqueoIntegridadEvidencia && (
        <div style={errorBoxStyle}>
          ⚠ La evidencia de la evaluación quedó en un estado inconsistente (solo algunos de los campos esperados están presentes). Contacta a TI — no se puede adjuntar una nueva evaluación firmada ni finalizar hasta resolverlo.
        </div>
      )}

      {estadoEvidencia === 'COMPLETA' && (
        <>
          <div style={{ fontWeight: 700, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '10px' }}>Evidencia firmada</div>
          <MetaEvidencia archivo={ev.nombre_archivo_evidencia} filas={[{ label: 'Adjuntada por', valor: ev.usuario_subida_evidencia }, { label: 'Fecha', valor: fmtFecha(ev.fecha_subida_evidencia) }]} />
          <div style={{ marginBottom: '16px' }}>
            <a className="btn-secondary" href={ev.evidencia_firma_url} target="_blank" rel="noreferrer">Ver evidencia</a>
          </div>
        </>
      )}

      {/* Secciones independientes de la evaluación firmada: Carta firmada,
          requisitos y Finalizar (el orden de adjuntado no se impone). */}
      <>
          {errorFinalizar && <div style={{ ...errorBoxStyle, marginBottom: '10px' }}>{errorFinalizar}</div>}
          {finalizando && faseFinalizar === 'verificando' && (
            <div style={{ fontSize: '12px', color: '#d97706', marginBottom: '10px' }}>No se pudo confirmar el resultado. Verificando estado...</div>
          )}

          {vista.requiereCarta && (
            <div style={{ borderTop: '1px solid #c7d9f5', paddingTop: '14px', marginBottom: '16px' }}>
              <div style={{ fontWeight: 700, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '10px' }}>Carta de aumento firmada</div>

              {vista.advertenciaCartaGeneradaParcial && (
                <div style={{ ...errorBoxStyle, marginBottom: '10px' }}>
                  ⚠ La carta de aumento generada quedó en un estado inconsistente. Contacta a TI antes de finalizar.
                </div>
              )}

              {vista.bloqueoIntegridadCartaFirmada && (
                <div style={errorBoxStyle}>
                  ⚠ La carta de aumento firmada quedó en un estado inconsistente (solo algunos de los campos esperados están presentes). Contacta a TI — no se puede adjuntar ni finalizar hasta resolverlo.
                </div>
              )}

              {vista.estadoCartaFirmada === 'COMPLETA' && (
                <>
                  <MetaEvidencia archivo={ev.nombre_archivo_carta_firmada} filas={[{ label: 'Adjuntada por', valor: ev.usuario_subida_carta_firmada }, { label: 'Fecha', valor: fmtFecha(ev.fecha_subida_carta_firmada) }]} />
                  <a className="btn-secondary" href={ev.evidencia_carta_firmada_url} target="_blank" rel="noreferrer">Ver carta firmada</a>
                </>
              )}

              {vista.puedeSubirCartaFirmada && (
                <>
                  <div style={{ fontSize: '13px', color: '#4b5563', marginBottom: '12px' }}>
                    El aumento fue aprobado: adjunta la carta de aumento firmada por el colaborador.
                  </div>
                  <div className="form-group" style={{ marginBottom: '10px' }}>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                      disabled={subiendoCartaFirmada}
                      onChange={e => handleSeleccionarCartaFirmada(e.target.files[0] || null)}
                    />
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>PDF, JPG o PNG · Máx. 5 MB</div>
                    {archivoCartaFirmada && !errorArchivoCartaFirmada && (
                      <div style={{ marginTop: '6px', fontSize: '12px', color: '#1d3d6b' }}>
                        📎 {archivoCartaFirmada.name} ({(archivoCartaFirmada.size / 1024).toFixed(1)} KB)
                      </div>
                    )}
                  </div>
                  {errorArchivoCartaFirmada && <div style={{ ...errorBoxStyle, marginBottom: '10px' }}>{errorArchivoCartaFirmada}</div>}
                  {subiendoCartaFirmada && faseCartaFirmada === 'verificando' && (
                    <div style={{ fontSize: '12px', color: '#d97706', marginBottom: '10px' }}>No se pudo confirmar el resultado. Verificando estado...</div>
                  )}
                  {errorSubidaCartaFirmada && <div style={{ ...errorBoxStyle, marginBottom: '10px' }}>{errorSubidaCartaFirmada}</div>}
                  <button className="btn-primary" disabled={!archivoCartaFirmada || !!errorArchivoCartaFirmada || subiendoCartaFirmada} onClick={handleAdjuntarCartaFirmada}>
                    {subiendoCartaFirmada ? 'Adjuntando carta firmada...' : 'Adjuntar carta firmada'}
                  </button>
                </>
              )}

              {vista.bloqueoIntegridadCartaFirmada && errorSubidaCartaFirmada && (
                <div style={{ ...errorBoxStyle, marginTop: '10px' }}>{errorSubidaCartaFirmada}</div>
              )}
            </div>
          )}

          {vista.requiereCarta && (
            <div style={{ fontSize: '12px', color: '#4b5563', marginBottom: '14px' }}>
              <div style={{ fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', marginBottom: '6px' }}>Requisitos para finalizar</div>
              <div>1. Evaluación firmada: {vista.estadoEvidencia === 'COMPLETA' ? '✔ COMPLETA' : vista.estadoEvidencia === 'PARCIAL' ? '⚠ INCONSISTENTE' : 'PENDIENTE'}</div>
              <div>2. Carta de aumento generada: {vista.estadoCartaGenerada === 'COMPLETA' ? '✔ COMPLETA' : vista.estadoCartaGenerada === 'PARCIAL' ? '⚠ INCONSISTENTE' : 'PENDIENTE'}</div>
              <div>3. Carta de aumento firmada: {vista.estadoCartaFirmada === 'COMPLETA' ? '✔ COMPLETA' : vista.estadoCartaFirmada === 'PARCIAL' ? '⚠ INCONSISTENTE' : 'PENDIENTE'}</div>
              <div>4. Finalizar evaluación: {vista.puedeFinalizar ? 'disponible' : 'NO disponible'}</div>
            </div>
          )}

          {/* Adjuntar evidencia y finalizar son dos acciones deliberadamente
              separadas -- nunca se finaliza automáticamente tras el upload
              (sección 12). El botón solo aparece si puedeFinalizarEvaluacion
              (con aumento APROBADO exige Carta generada y firmada COMPLETAS);
              el backend vuelve a validar. */}
          {vista.puedeFinalizar && !confirmandoCierre && (
            <button className="btn-primary" disabled={finalizando} onClick={() => setConfirmandoCierre(true)}>
              Finalizar evaluación
            </button>
          )}

          {vista.puedeFinalizar && confirmandoCierre && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px 14px' }}>
              <div style={{ fontSize: '13px', marginBottom: '10px' }}>
                <strong>¿Finalizar esta evaluación?</strong>
                <br />
                Confirma que la retroalimentación 1:1 fue realizada y que la evidencia firmada adjunta es la correcta.
                <br />
                Una vez finalizada, la evidencia no podrá reemplazarse desde este flujo.
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="button" className="btn-primary" disabled={finalizando} onClick={handleFinalizar}>
                  {finalizando ? 'Finalizando...' : 'Finalizar evaluación'}
                </button>
                <button type="button" className="btn-secondary" disabled={finalizando} onClick={() => setConfirmandoCierre(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          )}
      </>
    </CardSeccion>
  );
}

export { CierreEvaluacion };
